import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Map, View } from 'ol';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { XYZ, Vector as VectorSource, Cluster } from 'ol/source';
import { fromLonLat } from 'ol/proj';
import { Style, Icon, Stroke, Fill, Text, Circle as CircleStyle } from 'ol/style';
import { Feature } from 'ol';
import { FeatureLike } from 'ol/Feature';
import { Point, Geometry, Polygon, MultiPolygon, LineString } from 'ol/geom';
import type { Coordinate } from 'ol/coordinate';
import { Overlay } from 'ol';
import { useQuery } from '@tanstack/react-query';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { Drawer } from 'antd';
import MenuIcon from '@mui/icons-material/Menu';

import Sidebar from './Sidebar';
import EditLocationModal from './EditLocationModal';
import PrimaryMetricModal from './PrimaryMetricModal';
import RevenueTargetMetricsCard from './RevenueTargetMetricsCard';
import RevenueTargetDrawer from './RevenueTargetDrawer';
import StandardMapView, { StandardMapDrawerContent } from './views/StandardMapView';
import FrontsiteMapView from './views/FrontsiteMapView';
import {
  RevenueLegend,
  RevenueDrawerExtras,
  RevenueTopClientsContent,
  RevenueChurnedContent,
} from './views/RevenueMapView';
import { SalesPipelineLegend, SalesPipelineDrawerContent } from './views/SalesPipelineMapView';
import {
  PipelineDrawerAggregates,
  PipelineDrawerRow,
  PipelineSummary,
  RevenueChurnedSummary,
  LocationTypeSummary,
} from './views/types';
import {
  fetchLocations,
  fetchWarehouses,
  fetchVendors,
  fetchProspects,
  fetchClientSummary,
  fetchPrimaryMetrics,
  fetchSalesPipeline,
  fetchProductCompanyLocations,
  fetchSupplyHubMap,
  fetchFrontsiteClients,
} from '../../services/api';
import {
  FilterState,
  Location,
  LocationResponse,
  Warehouse,
  Vendor,
  Prospect,
  LocationType,
  SalesPipelineResponse,
  SalesPipelineEntry,
  SalesPipelineStage,
  SalesPipelineStageCounts,
  SalesPipelineStageSummary,
  SalesPipelineStageValues,
  ProductCompanyLocation,
  ProductCompanyLocationsResponse,
  SupplyHubMapResponse,
  SupplyHubWarehouse,
  FrontsiteClientsResponse,
  FrontsiteOrder,
} from '../../types';
import type { SupplyHubPurchaseOrder } from '../../types';
import { buildPopupTableContent, PopupTableRow, formatCurrency, formatExactCurrency, formatExactNumber, handleEntityLinkClick } from './utils';
import '../../styles/map.css';
import FilterSection from '../MapComponent/Sidebar/FilterSection';
import {
  ProductMapDrawer,
  ProductDrawerExtras,
  ProductTopProductsDrawer,
  ProductCityInsightsDrawer,
  ProductAggregate,
  CityProductInsight,
  buildProductAggregates,
  buildProductCityInsights,
  ProductMarkerBucket,
  ProductLegend,
  ProductLegendBucketSummary,
} from './views/ProductMapDrawer';
import {
  SUPPLY_HUB_SHIPPING_MODE_CONFIG,
  SupplyHubShippingMode,
  SupplyHubVendorGroup,
  SupplyHubDrawerContext,
  resolveSupplyHubShippingMode,
  buildSupplyHubWarehouseKey,
} from './views/SupplyHubTypes';
import type { SupplyHubShippingSummary } from './views/SupplyHubTypes';
import { SupplyHubLegend, SupplyHubDrawerContent } from './views/SupplyHubMapView';
import FrontsiteDrawerContent from './views/FrontsiteDrawerContent';
import CustomerInsightsDrawer from './views/CustomerInsightsDrawer';
import FrontsiteOrderDetails from './views/FrontsiteOrderDetails';

import {
  ALL_LOCATION_TYPES,
  CHURNED_PAGE_SIZE,
  LOCATION_TYPE_META,
  LOCATION_TYPE_ORDER,
  PIPELINE_STAGE_COUNT_KEY,
  PIPELINE_STAGE_DATE_KEY,
  PIPELINE_STAGE_SEQUENCE,
  PIPELINE_STAGE_VALUE_KEY,
  REVENUE_TARGET_DRAWER_PAGE_SIZE,
  SALES_PIPELINE_STAGE_COLORS,
} from './constants';
import {
  PipelineDrawerMetricsOverride,
  RevenueCompanyDetailRow,
  RevenueRepDetailRow,
  RevenueTargetCompanyEntry,
  RevenueTargetDrawerRow,
  RevenueTargetRepEntry,
  TableColumnKey,
  createDefaultTableColumnVisibility,
} from './types';
import {
  DEFAULT_PRODUCT_BUCKETS,
  buildProductLocationKey,
  collectProductLocationIntoMap,
  computeLocationUniqueProductCount,
  computeProductDrawerTitle,
  deriveProductMarkerBuckets,
  parseNumericAmount,
  selectProductMarkerBucket,
} from './helpers/product';
import {
  computeOpenSOMetrics,
  formatInvoiceDate,
  formatRelativeDate,
  formatShortCurrency,
  getPipelineStageValue,
  getRevenueTierColor,
  isFiniteNumber,
  normalizeStageSummary,
  parseChurnAmount,
  parseInvoiceDate,
} from './helpers/pipeline';
import { darkenHexColor, hexToRgba, lightenHexColor } from './helpers/color';
import { usePipelineStyles } from './hooks/usePipelineStyles';




interface MapComponentProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  primaryMetricModalVisible: boolean;
  onPrimaryMetricModalClose: () => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
  filters,
  onFiltersChange,
  primaryMetricModalVisible,
  onPrimaryMetricModalClose
}) => {
  const [showMap, setShowMap] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<Map | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);
  const revenueAutoFitRef = useRef(false);
  const productAutoFitRef = useRef(false);
  const frontsiteAutoFitRef = useRef(false);
  const previousMapViewRef = useRef<FilterState['mapView'] | undefined>(filters.mapView);
  const [popup, setPopup] = useState<Overlay | null>(null);
  // Right-side table drawer
  const [tableDrawerVisible, setTableDrawerVisible] = useState(false);
  const [tableDrawerHTML, setTableDrawerHTML] = useState('');
  const tableDrawerRef = useRef<HTMLDivElement>(null);
  const productTableVisibilityBackupRef = useRef<Record<TableColumnKey, boolean> | null>(null);
  const [productDrawerVisible, setProductDrawerVisible] = useState(false);
  const [productDrawerLocations, setProductDrawerLocations] = useState<ProductCompanyLocation[]>([]);
  const [productDrawerTitle, setProductDrawerTitle] = useState<string>('');
  const [topProductsDrawerVisible, setTopProductsDrawerVisible] = useState(false);
  const [cityInsightsDrawerVisible, setCityInsightsDrawerVisible] = useState(false);
  const [productMapRequested, setProductMapRequested] = useState(false);
  const [supplyHubDrawerVisible, setSupplyHubDrawerVisible] = useState(false);
  const [supplyHubDrawerContext, setSupplyHubDrawerContext] = useState<SupplyHubDrawerContext | null>(null);
  const [supplyHubModeFilter, setSupplyHubModeFilter] = useState<'all' | SupplyHubShippingMode>('all');
  const supplyHubStyleCache = useRef({
    warehouse: new globalThis.Map<string, Style[]>(),
    vendor: new globalThis.Map<string, Style[]>(),
    route: new globalThis.Map<string, Style[]>(),
    routeIcon: new globalThis.Map<string, Icon>(),
  });

  useEffect(() => {
    if (filters.mapView !== 'supply-hub') {
      setSupplyHubDrawerVisible(false);
      setSupplyHubDrawerContext(null);
    }
  }, [filters.mapView]);
  const pipelineSummaryRef = useRef<PipelineSummary | null>(null);
  const pipelineEntryCountRef = useRef<number>(0);
  const [pipelineDrawerMetricsOverride, setPipelineDrawerMetricsOverride] = useState<PipelineDrawerMetricsOverride | null>(null);
  useEffect(() => {
    if (filters.mapView !== 'supply-hub') {
      setSupplyHubModeFilter('all');
    }
  }, [filters.mapView]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (filters.mapView === 'product') {
      setProductMapRequested(true);
    }
  }, [filters.mapView]);

  const handleSupplyHubModeChange = useCallback((mode: 'all' | SupplyHubShippingMode) => {
    setSupplyHubModeFilter((current) => {
      if (mode === 'all') {
        return 'all';
      }
      return current === mode ? 'all' : mode;
    });
  }, []);

const [pipelineDrawerEntries, setPipelineDrawerEntries] = useState<SalesPipelineEntry[]>([]);
const [pipelineDrawerContext, setPipelineDrawerContext] = useState<'overall' | 'subset'>('overall');
const [pipelineStageFilter, setPipelineStageFilter] = useState<SalesPipelineStage | 'ALL'>('ALL');
const handlePipelineStageToggle = useCallback((stage: SalesPipelineStage | 'ALL') => {
  setPipelineStageFilter((current: SalesPipelineStage | 'ALL') => {
    if (stage === 'ALL') return 'ALL';
    return current === stage ? 'ALL' : stage;
  });
}, []);
const handleFrontsiteOrderView = useCallback((order: FrontsiteOrder) => {
  setFrontsiteDetailOrder(order);
  setFrontsiteDetailDrawerVisible(true);
}, []);
const handleFrontsiteOrderDetailClose = useCallback(() => {
  setFrontsiteDetailDrawerVisible(false);
  setFrontsiteDetailOrder(null);
}, []);
const openFrontsiteDrawerOrders = useCallback((orders: FrontsiteOrder[]) => {
  setFrontsiteDrawerOrders(orders);
  setFrontsiteDetailDrawerVisible(false);
  setFrontsiteDetailOrder(null);
  setTableDrawerHTML('');
  setTableRows([]);
  setTableSearch('');
  setTableDrawerVisible(orders.length > 0);
  setPipelineDrawerMetricsOverride(null);
}, []);

  const handleLocationTypeChipToggle = useCallback(
    (type: LocationType | 'all') => {
      const current = Array.isArray(filters.locationType) ? filters.locationType : [];
      const next: LocationType[] =
        type === 'all'
          ? [...ALL_LOCATION_TYPES]
          : [type as LocationType];

      const currentKey = JSON.stringify([...current].sort());
      const nextKey = JSON.stringify([...next].sort());
      if (currentKey === nextKey) {
        return;
      }

      onFiltersChange({
        ...filters,
        locationType: next,
      });
    },
    [filters, onFiltersChange],
  );

  
  const {
    pipelineClusterStyle,
    pipelineStyleCache,
  } = usePipelineStyles({
    mapInstance,
    pipelineStageFilter,
    pipelineSummaryRef,
    pipelineEntryCountRef,
  });

  const buildPipelineTableRow = React.useCallback((entry: SalesPipelineEntry): PopupTableRow => {
    const stageValue = entry.stage_values?.net_invoice_value ?? entry.stage_values?.sales_order_value ?? entry.stage_values?.quotation_value ?? 0;
    const stageCounts = entry.stage_counts ?? { enquiry: 0, quotation: 0, sales_order: 0, invoice: 0 };
    const stageDates = entry.stage_dates ?? { last_enquiry: null, last_quotation: null, last_sales_order: null, last_invoice: null };
    const stageLabel = entry.current_stage ? entry.current_stage.toLowerCase().replace('_', ' ') : 'enquiry';

    const formatDate = (value?: string | null) => {
      if (!value) return '-';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    };

    const countsLabel = `ENQ ${stageCounts.enquiry} | QUO ${stageCounts.quotation} | SO ${stageCounts.sales_order} | INV ${stageCounts.invoice}`;
    const datesLabel = `ENQ ${formatDate(stageDates.last_enquiry)} | QUO ${formatDate(stageDates.last_quotation)} | SO ${formatDate(stageDates.last_sales_order)} | INV ${formatDate(stageDates.last_invoice)}`;

    return {
      type: 'client',
      name: entry.company || '',
      userid: entry.clientid ? String(entry.clientid) : '',
      address: entry.address || '',
      city: entry.city || '',
      state: entry.state || '',
      zip: entry.zipcode || '',
      country: entry.country?.short_name || '',
      staff: entry.staff_name ? `${entry.staff_name} - ${stageLabel}` : stageLabel,
      orders: stageCounts.sales_order ?? undefined,
      products: stageCounts.quotation ?? undefined,
      total_amount: stageValue,
      email: `Counts: ${countsLabel}`,
      phone: `Last: ${datesLabel}`,
      website_validation_notes: entry.website_validation_notes || '',
      website_found_address: entry.website_found_address || '',
      website_found_city: entry.website_found_city || '',
      website_found_state: entry.website_found_state || '',
      website_found_country: entry.website_found_country || '',
    };
  }, []);

  const mapFeatureToPopupRow = useCallback((feature: FeatureLike): PopupTableRow => {
    const props: any = (feature as any).get ? (feature as any).get('properties') || {} : {};
    const pipelineEntry = props.pipelineEntry || (feature as any).get?.('pipelineEntry');

    if (pipelineEntry) {
      return buildPipelineTableRow(pipelineEntry as SalesPipelineEntry);
    }

    const fallback = props?.properties || {};
    const merged = { ...fallback, ...props };
    const type = (merged.type as PopupTableRow['type']) || 'client';

    if (type === 'product') {
      const productLocation =
        (merged.productLocation as ProductCompanyLocation | undefined) ??
        (fallback.productLocation as ProductCompanyLocation | undefined) ??
        ((feature as any).get?.('productLocation') as ProductCompanyLocation | undefined);

      const resolvedUserId =
        merged.userid ??
        fallback.userid ??
        (productLocation && productLocation.userid !== undefined ? productLocation.userid : undefined);

      const locationKey = productLocation
        ? buildProductLocationKey(productLocation)
        : merged.location_id || fallback.location_id || (resolvedUserId ? String(resolvedUserId) : '');

      const totalQuantityRaw =
        merged.total_quantity ??
        merged.totalQuantity ??
        fallback.total_quantity ??
        fallback.totalQuantity ??
        (feature as any).get?.('totalQuantity');

      const totalAmountRaw =
        merged.total_amount ??
        merged.totalAmount ??
        fallback.total_amount ??
        fallback.totalAmount ??
        (feature as any).get?.('totalAmount');

      const uniqueProductsRaw =
        merged.uniqueProductCount ??
        merged.unique_product_count ??
        fallback.uniqueProductCount ??
        fallback.unique_product_count ??
        (feature as any).get?.('uniqueProductCount');

      const totalQuantity = Number(totalQuantityRaw ?? 0);
      const totalAmount = Number(totalAmountRaw ?? 0);
      const uniqueProductsValue =
        uniqueProductsRaw !== undefined && uniqueProductsRaw !== null
          ? Number(uniqueProductsRaw)
          : productLocation
          ? computeLocationUniqueProductCount(productLocation)
          : undefined;

      return {
        type: 'product',
        name: merged.company || productLocation?.company || '',
        userid:
          resolvedUserId !== undefined && resolvedUserId !== null && resolvedUserId !== ''
            ? String(resolvedUserId)
            : undefined,
        location_id: locationKey || undefined,
        address: '',
        city: merged.city || productLocation?.city || '',
        state: merged.state || productLocation?.state || '',
        zip: '',
        country:
          typeof merged.country === 'string'
            ? merged.country
            : merged.country?.short_name || productLocation?.country || '',
        staff: '',
        orders: Number.isFinite(totalQuantity) ? totalQuantity : undefined,
        products: Number.isFinite(Number(uniqueProductsValue)) ? Number(uniqueProductsValue) : undefined,
        open_so: '',
        target: undefined,
        target_year: undefined,
        target_achievement: undefined,
        total_amount: Number.isFinite(totalAmount) ? totalAmount : undefined,
        invoice_created_date: '',
        email: '',
        phone: '',
        website_validation_notes: '',
        website_found_address: '',
        website_found_city: '',
        website_found_state: '',
        website_found_country: '',
      };
    }

    return {
      type,
      name: merged.company || merged.name || '',
      userid: merged.userid || fallback.userid,
      location_id: merged.id || fallback.id || '',
      address: merged.address || '',
      city: merged.city || '',
      state: merged.state || '',
      zip: merged.zip || '',
      country: typeof merged.country === 'string'
        ? merged.country
        : merged.country?.short_name || fallback.country?.short_name || '',
      staff: merged.staff_name || '',
      orders: merged.order_count ?? fallback.order_count ?? undefined,
      products: merged.unique_products ?? fallback.unique_products ?? undefined,
      quotation_count: merged.quotation_count ?? fallback.quotation_count ?? undefined,
      enquiry_count: merged.enquiry_count ?? fallback.enquiry_count ?? undefined,
      open_so: typeof merged.open_so === 'string'
        ? merged.open_so
        : (typeof fallback.open_so === 'string' ? fallback.open_so : ''),
      target: merged.target ?? fallback.target ?? undefined,
      target_year: merged.target_year ?? fallback.target_year ?? undefined,
      target_achievement: merged.target_achievement ?? fallback.target_achievement ?? undefined,
      total_amount: merged.total_amount ?? fallback.total_amount ?? undefined,
      invoice_created_date: merged.invoice_created_date || fallback.invoice_created_date || '',
      email: merged.email_1 || fallback.email_1 || '',
      phone: merged.phonenumber || fallback.phonenumber || '',
      website_validation_notes: merged.website_validation_notes || fallback.website_validation_notes || '',
      website_found_address: merged.website_found_address || fallback.website_found_address || '',
      website_found_city: merged.website_found_city || fallback.website_found_city || '',
      website_found_state: merged.website_found_state || fallback.website_found_state || '',
      website_found_country: merged.website_found_country || fallback.website_found_country || '',
    };
  }, [filters.mapView, buildPipelineTableRow]);

  const extractPipelineEntry = useCallback((feature: FeatureLike): SalesPipelineEntry | undefined => {
    if (!feature || typeof (feature as any).get !== 'function') {
      return undefined;
    }
    const direct = (feature as any).get('pipelineEntry') as SalesPipelineEntry | undefined;
    if (direct) return direct;
    const props = (feature as any).get('properties');
    return props?.pipelineEntry as SalesPipelineEntry | undefined;
  }, []);

  const formatPipelineDate = (value?: string | null) => {
    if (!value) return '?';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '?' : date.toLocaleDateString();
  };

  const formatPipelineCount = (value?: number | null) => {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric)) return '0';
    return Math.round(numeric).toLocaleString();
  };

  const formatDateValue = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [tableColumnVisibility, setTableColumnVisibility] = useState<Record<TableColumnKey, boolean>>(
    () => createDefaultTableColumnVisibility(),
  );
  const buildTabColumnVisibility = (type: PopupTableRow['type']) => {
    const base = createDefaultTableColumnVisibility();
    base.orders = false;
    base.products = false;
    if (type === 'vendor') {
      (Object.keys(base) as TableColumnKey[]).forEach((key) => {
        base[key] = false;
      });
      base.name = true;
      base.address = true;
      base.city = true;
      base.state = true;
      base.action = true;
      return base;
    }
    if (type === 'prospect') {
      base.quotation_count = true;
      base.enquiry_count = true;
    }
    if (type === 'warehouse') {
      base.address = true;
      base.city = true;
      base.state = true;
      base.staff = false;
      base.target = false;
      base.target_year = false;
      base.target_achievement = false;
      base.total_amount = false;
      base.revenue_share = false;
      base.action = false;
    }
    return base;
  };
  const [tableColumnVisibilityByTab, setTableColumnVisibilityByTab] = useState<
    Record<PopupTableRow['type'], Record<TableColumnKey, boolean>>
  >(() => ({
    client: buildTabColumnVisibility('client'),
    prospect: buildTabColumnVisibility('prospect'),
    vendor: buildTabColumnVisibility('vendor'),
    warehouse: buildTabColumnVisibility('warehouse'),
    product: buildTabColumnVisibility('product'),
  }));
  const [tableRows, setTableRows] = useState<PopupTableRow[]>([]);
  const [tableSearch, setTableSearch] = useState('');
  const [drawerActiveTab, setDrawerActiveTab] = useState<PopupTableRow['type']>('client');
  const drawerActiveTabRef = useRef<PopupTableRow['type']>('client');
  const activeTabColumnVisibility =
    tableColumnVisibilityByTab[drawerActiveTab] ?? buildTabColumnVisibility(drawerActiveTab);
  const [useTabData, setUseTabData] = useState(false);
  const [drawerRowsByType, setDrawerRowsByType] = useState<Record<PopupTableRow['type'], PopupTableRow[]>>({
    client: [],
    prospect: [],
    vendor: [],
    warehouse: [],
    product: [],
  });
  const [customerInsightsOpen, setCustomerInsightsOpen] = useState(false);
  const [customerInsightsContext, setCustomerInsightsContext] = useState<{
    id: string;
    name?: string;
  } | null>(null);
  const [churnedPage, setChurnedPage] = useState(1);
  const [topClientsDrawerVisible, setTopClientsDrawerVisible] = useState(false);
  const [churnedDrawerVisible, setChurnedDrawerVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingLocationData, setEditingLocationData] = useState<{
    id: string;
    userid: string;
    company: string;
    address: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    website_validation_notes: string;
    website_found_address: string;
    website_found_city: string;
    website_found_state: string;
    website_found_country: string;
  } | null>(null);
  const [revenueTargetCardDismissed, setRevenueTargetCardDismissed] = useState(false);
  const [revenueTargetDrawerVisible, setRevenueTargetDrawerVisible] = useState(false);
  const [revenueTargetDrawerPage, setRevenueTargetDrawerPage] = useState(1);
  const [revenueCompanyDetail, setRevenueCompanyDetail] = useState<{
    open: boolean;
    stateName?: string;
    rows: RevenueCompanyDetailRow[];
  }>({
    open: false,
    stateName: undefined,
    rows: [],
  });
  const [revenueRepDetail, setRevenueRepDetail] = useState<{
    open: boolean;
    stateName?: string;
    rows: RevenueRepDetailRow[];
  }>({
    open: false,
    stateName: undefined,
    rows: [],
  });
  const [frontsiteDrawerOrders, setFrontsiteDrawerOrders] = useState<FrontsiteOrder[]>([]);
  const [frontsiteDetailOrder, setFrontsiteDetailOrder] = useState<FrontsiteOrder | null>(null);
  const [frontsiteDetailDrawerVisible, setFrontsiteDetailDrawerVisible] = useState(false);

  useEffect(() => {
    drawerActiveTabRef.current = drawerActiveTab;
  }, [drawerActiveTab]);

  const handleStandardRowEdit = useCallback((row: PopupTableRow) => {
    if (!row.userid) {
      return;
    }

    setEditingLocationData({
      id: row.location_id || row.userid,
      userid: row.userid,
      company: row.name || '',
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      country: row.country || '',
      zip: row.zip || '',
      website_validation_notes: row.website_validation_notes || '',
      website_found_address: row.website_found_address || '',
      website_found_city: row.website_found_city || '',
      website_found_state: row.website_found_state || '',
      website_found_country: row.website_found_country || '',
    });
    setEditModalVisible(true);
  }, []);

  const handleClientSelect = useCallback((row: PopupTableRow) => {
    const resolvedId = row.userid || row.location_id;
    if (!resolvedId) {
      return;
    }
    setCustomerInsightsContext({ id: String(resolvedId), name: row.name || undefined });
    setCustomerInsightsOpen(true);
  }, []);

  const groupRowsByType = useCallback((rows: PopupTableRow[]) => {
    const grouped: Record<PopupTableRow['type'], PopupTableRow[]> = {
      client: [],
      prospect: [],
      vendor: [],
      warehouse: [],
      product: [],
    };
    rows.forEach((row) => {
      grouped[row.type].push(row);
    });
    return grouped;
  }, []);

  const openReactTableDrawer = useCallback((rows: PopupTableRow[], resetSearch = true) => {
    const presentTypes = Array.from(new Set(rows.map((row) => row.type)));
    if (presentTypes.length === 1) {
      setDrawerActiveTab(presentTypes[0]);
    } else if (!presentTypes.includes(drawerActiveTabRef.current)) {
      setDrawerActiveTab('client');
    }
    setUseTabData(false);
    setDrawerRowsByType(groupRowsByType(rows));
    setTableRows(rows);
    if (resetSearch) {
      setTableSearch('');
    }
    setTableDrawerHTML('');
    setTableDrawerVisible(true);
  }, [groupRowsByType]);


  useEffect(() => {
    const handleExternalEdit = (event: Event) => {
      const customEvent = event as CustomEvent<PopupTableRow>;
      if (!customEvent.detail) {
        return;
      }
      handleStandardRowEdit(customEvent.detail);
    };
    window.addEventListener('open-edit-location', handleExternalEdit as EventListener);
    return () => {
      window.removeEventListener('open-edit-location', handleExternalEdit as EventListener);
    };
  }, [handleStandardRowEdit]);

  // Show the map directly with default filters
  useEffect(() => {
    // Set default filters: United States and California
    if (!filters.country && !filters.state) {
      onFiltersChange({
        ...filters,
        country: 'United States',
        state: 'California'
      });
      return;
    }
    
    // Show map directly
    setShowMap(true);
  }, [filters, onFiltersChange]);

  useEffect(() => {
    if (filters.mapView === 'revenue' && previousMapViewRef.current !== 'revenue') {
      setRevenueTargetCardDismissed(false);
    }
    if (filters.mapView !== 'revenue') {
      setRevenueTargetDrawerVisible(false);
    }
    previousMapViewRef.current = filters.mapView;
  }, [filters.mapView]);

  useEffect(() => {
    if (filters.mapView === 'revenue') {
      revenueAutoFitRef.current = false;
    } else if (filters.mapView === 'product') {
      productAutoFitRef.current = false;
    } else if (filters.mapView === 'frontsite') {
      frontsiteAutoFitRef.current = false;
    }
  }, [
    filters.mapView,
    filters.country,
    filters.state,
    filters.city,
    filters.searchTerm,
    filters.productSearch,
    filters.startDate,
    filters.endDate,
    filters.dateRangePreset,
  ]);

useEffect(() => {
    if (filters.mapView !== 'frontsite') {
      setFrontsiteDrawerOrders([]);
      setFrontsiteDetailOrder(null);
      setFrontsiteDetailDrawerVisible(false);
    }
  }, [filters.mapView]);


  useEffect(() => {
    const productOverrides: Partial<Record<TableColumnKey, boolean>> = {
      address: false,
      city: true,
      state: true,
      zip: false,
      country: true,
      staff: false,
      orders: true,
      products: true,
      target: false,
      target_year: false,
      target_achievement: false,
      total_amount: true,
      revenue_share: false,
      email: false,
      phone: false,
      action: false,
    };

    if (filters.mapView === 'product') {
      if (!productTableVisibilityBackupRef.current) {
        productTableVisibilityBackupRef.current = { ...tableColumnVisibility };
        setTableColumnVisibility((prev) => ({
          ...prev,
          ...productOverrides,
        }));
      }
    } else if (productTableVisibilityBackupRef.current) {
      const restore = productTableVisibilityBackupRef.current;
      productTableVisibilityBackupRef.current = null;
      setTableColumnVisibility(restore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.mapView]);

  useEffect(() => {
    const hideTargets = filters.dateRangePreset === 'all-dates';
    setTableColumnVisibilityByTab((prev) => ({
      ...prev,
      client: {
        ...prev.client,
        target: !hideTargets,
        target_year: !hideTargets,
        target_achievement: !hideTargets,
      },
      prospect: {
        ...prev.prospect,
        target: !hideTargets,
        target_year: !hideTargets,
        target_achievement: !hideTargets,
      },
    }));
  }, [filters.dateRangePreset]);

  // Listen for requests from child components to open the table drawer
  useEffect(() => {
    const handler = (e: Event) => {
      const anyEvent = e as any;
      const rows: PopupTableRow[] = anyEvent?.detail?.rows || [];
      try {
        if (filters.mapView === 'standard' || filters.mapView === 'revenue') {
          openReactTableDrawer(rows);
          return;
        }
        const content = buildPopupTableContent(rows, {
          page: 1,
          pageSize: 10,
          search: '',
          columnVisibility: tableColumnVisibility,
          showColumnToggle: true,
        });
        setTableDrawerHTML(content);
        setTableDrawerVisible(true);
      } catch (err) {
        console.error('Failed to open table drawer:', err);
      }
    };
    window.addEventListener('open-table-drawer', handler as EventListener);
    return () => window.removeEventListener('open-table-drawer', handler as EventListener);
  }, [filters.mapView, tableColumnVisibility, openReactTableDrawer]);

  const revenueDrawerMetrics = useMemo(() => {
    const norm = (s?: string) => (s || '').toLowerCase();
    const searchValue = norm(tableSearch);
    const matches = (row: PopupTableRow) => {
      if (!searchValue) return true;
      const parts = [row.name, row.city, row.state, row.country, row.staff, row.email, row.phone]
        .filter(Boolean)
        .map((value) => norm(String(value)));
      return parts.some((part) => part.includes(searchValue));
    };
    const parseAmount = (value?: string | number) => {
      if (value === undefined || value === null || value === '') return 0;
      if (typeof value === 'number') return value;
      const trimmed = value.trim();
      if (/m$/i.test(trimmed)) return parseFloat(trimmed) * 1_000_000;
      if (/k$/i.test(trimmed)) return parseFloat(trimmed) * 1_000;
      const numeric = parseFloat(trimmed.replace(/[^0-9.-]+/g, ''));
      return Number.isFinite(numeric) ? numeric : 0;
    };

    const filtered = (tableRows || []).filter(matches);
    const clientsCount = filtered.filter((row): row is PopupTableRow & { type: 'client' } => row.type === 'client').length;
    const prospectsCount = filtered.filter((row): row is PopupTableRow & { type: 'prospect' } => row.type === 'prospect').length;
    const totalAchievement = filtered.reduce<number>((sum, row) => sum + parseAmount(row.total_amount), 0);
    const revenue = totalAchievement;
    const totalTarget = filtered.reduce<number>((sum, row) => sum + parseAmount(row.target), 0);
    const targetPercent = totalTarget > 0 ? (totalAchievement / totalTarget) * 100 : 0;
    const targetYears = filtered
      .map((row) => row.target_year)
      .filter((value): value is string | number => value !== null && value !== undefined && value !== '');
    const conversionRate = prospectsCount > 0 ? (clientsCount / prospectsCount) * 100 : 0;
    const avgDeal = clientsCount > 0 ? revenue / clientsCount : 0;

    return {
      revenue,
      clientsCount,
      prospectsCount,
      conversionRate,
      avgDeal,
      totalTarget,
      totalAchievement,
      targetPercent,
      targetYears,
    };
  }, [tableRows, tableSearch]);

  const topClientRows = useMemo(() => {
    if (filters.mapView === 'sales-pipeline') return [];
    const norm = (value?: string) => (value || '').toLowerCase();
    const searchValue = norm(tableSearch);
    const matches = (row: PopupTableRow) => {
      if (!searchValue) return true;
      const parts = [row.name, row.city, row.state, row.country, row.staff, row.email, row.phone]
        .filter(Boolean)
        .map((value) => norm(String(value)));
      return parts.some((part) => part.includes(searchValue));
    };
    const filtered = (tableRows || [])
      .filter((row): row is PopupTableRow & { type: 'client' } => row.type === 'client')
      .filter(matches);
    if (filtered.length <= 10) return [];
    return [...filtered]
      .sort((a, b) => parseChurnAmount(b.total_amount) - parseChurnAmount(a.total_amount))
      .slice(0, 10);
  }, [filters.mapView, tableRows, tableSearch]);

  const churnedClients = useMemo(() => {
    const norm = (value?: string) => (value || '').toLowerCase();
    const matches = (row: PopupTableRow, query: string) => {
      if (!query) return true;
      const parts = [row.name, row.city, row.state, row.country]
        .filter(Boolean)
        .map((value) => norm(String(value)));
      return parts.some((part) => part.includes(norm(query)));
    };

    const filtered = (tableRows || [])
      .filter((row): row is PopupTableRow & { type: 'client' } => row.type === 'client')
      .filter((row) => matches(row, tableSearch));

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);

    return filtered.filter((row) => {
      const invoiceDate = parseInvoiceDate(row.invoice_created_date);
      return invoiceDate ? invoiceDate < cutoff : false;
    });
  }, [tableRows, tableSearch]);

  const {
    data: salesPipelineResponse,
    isLoading: isLoadingSalesPipeline,
  } = useQuery<SalesPipelineResponse>({
    queryKey: [
      'sales-pipeline',
      filters.country,
      filters.state,
      filters.city,
      filters.salesRep,
      filters.startDate,
      filters.endDate,
      filters.productSearch,
    ],
    queryFn: () =>
      fetchSalesPipeline({
        country: filters.country || undefined,
        state: filters.state || undefined,
        city: filters.city || undefined,
        salesRep: filters.salesRep || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        productSearch: filters.mapView === 'sales-pipeline' ? filters.productSearch || undefined : undefined,
      }),
    enabled: showMap && filters.mapView === 'sales-pipeline',
    staleTime: 300000,
  });

  const pipelineSummary = useMemo<PipelineSummary>(() => {
    const aggregateCounts: Partial<SalesPipelineStageCounts> = salesPipelineResponse?.stage_counts ?? {};
    const aggregateValues: Partial<SalesPipelineStageValues> = salesPipelineResponse?.stage_values ?? {};
    const pipelineEntries = salesPipelineResponse?.pipeline_data ?? [];

    const stageSummaryMap = PIPELINE_STAGE_SEQUENCE.reduce((acc, stage) => {
      acc[stage] = normalizeStageSummary(salesPipelineResponse?.stage_summary?.[stage]);
      return acc;
    }, {} as Record<SalesPipelineStage, SalesPipelineStageSummary>);

    const stageMap = PIPELINE_STAGE_SEQUENCE.reduce((acc, stage) => {
      acc[stage] = { count: 0, value: 0 };
      return acc;
    }, {} as PipelineSummary['stageMap']);

    pipelineEntries.forEach((entry) => {
      const coords = entry.coordinates;
      const hasValidCoords =
        Array.isArray(coords) &&
        coords.length === 2 &&
        typeof coords[0] === 'number' &&
        typeof coords[1] === 'number' &&
        Number.isFinite(coords[0]) &&
        Number.isFinite(coords[1]);

      if (!hasValidCoords) {
        return;
      }

      const counts = entry.stage_counts ?? {};
      const values = entry.stage_values ?? {};
      const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
      const totals = stageMap[stage];
      if (!totals) {
        return;
      }

      const countKey = PIPELINE_STAGE_COUNT_KEY[stage];
      const rawCount = Number((counts as SalesPipelineEntry['stage_counts'])[countKey] ?? 0);
      if (Number.isFinite(rawCount) && rawCount > 0) {
        totals.count += rawCount;
      } else {
        totals.count += 1;
      }

      const valueKey = PIPELINE_STAGE_VALUE_KEY[stage];
      if (valueKey) {
        const rawValue = Number((values as SalesPipelineEntry['stage_values'])[valueKey] ?? 0);
        if (Number.isFinite(rawValue) && rawValue !== 0) {
          totals.value += rawValue;
        }
      }
    });

    PIPELINE_STAGE_SEQUENCE.forEach((stage) => {
      const totals = stageMap[stage];
      const summary = stageSummaryMap[stage];
      const countKey = PIPELINE_STAGE_COUNT_KEY[stage];
      const valueKey = PIPELINE_STAGE_VALUE_KEY[stage];

      if (totals.count <= 0) {
        const aggregateCountRaw = Number(aggregateCounts?.[countKey] ?? 0);
        if (Number.isFinite(aggregateCountRaw) && aggregateCountRaw > 0) {
          totals.count = aggregateCountRaw;
        } else if (Number.isFinite(summary.count) && summary.count > 0) {
          totals.count = summary.count;
        }
      }

      if (totals.value === 0) {
        if (valueKey) {
          const aggregateValueRaw = Number(aggregateValues?.[valueKey] ?? 0);
          if (Number.isFinite(aggregateValueRaw) && aggregateValueRaw !== 0) {
            totals.value = aggregateValueRaw;
          }
        }
        if (totals.value === 0 && stage === 'INVOICED') {
          const invoiceFallback = Number(aggregateValues?.invoice_value ?? 0);
          if (Number.isFinite(invoiceFallback) && invoiceFallback !== 0) {
            totals.value = invoiceFallback;
          }
        }
        if (totals.value === 0 && Number.isFinite(summary.value) && summary.value !== 0) {
          totals.value = summary.value;
        }
      }
    });

    const stages = PIPELINE_STAGE_SEQUENCE.map((stage) => ({
      stage,
      count: stageMap[stage]?.count ?? 0,
      value: stageMap[stage]?.value ?? 0,
    }));

    const totalFromCounts = PIPELINE_STAGE_SEQUENCE.reduce((sum, stage) => {
      const countKey = PIPELINE_STAGE_COUNT_KEY[stage];
      const numeric = Number(aggregateCounts?.[countKey] ?? 0);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);

    const derivedTotalClients = totalFromCounts > 0
      ? totalFromCounts
      : stages.reduce((sum, stage) => sum + stage.count, 0);

    const fallbackTotalClientsRaw = Number(salesPipelineResponse?.stage_counts?.total_enquiry_items ?? 0);
    const fallbackTotalClients = Number.isFinite(fallbackTotalClientsRaw) ? fallbackTotalClientsRaw : 0;

    const totalClients =
      derivedTotalClients > 0
        ? derivedTotalClients
        : fallbackTotalClients > 0
          ? fallbackTotalClients
          : Number(salesPipelineResponse?.total_clients ?? 0);

    const derivedTotalValue = stages.reduce((sum, stage) => sum + stage.value, 0);
    let totalValue = Number(salesPipelineResponse?.total_pipeline_value ?? 0);
    if (!Number.isFinite(totalValue) || totalValue <= 0) {
      totalValue = derivedTotalValue;
    }

    return {
      totalValue,
      totalClients,
      stages,
      stageMap,
    };
  }, [salesPipelineResponse]);

  useEffect(() => {
    pipelineSummaryRef.current = pipelineSummary;
  }, [pipelineSummary]);

  useEffect(() => {
    const entryCount = salesPipelineResponse?.pipeline_data?.length ?? 0;
    pipelineEntryCountRef.current = entryCount;
  }, [salesPipelineResponse?.pipeline_data?.length]);

  const computePipelineDrawerMetricsOverride = useCallback(
    (entries: SalesPipelineEntry[]): PipelineDrawerMetricsOverride | null => {
      if (pipelineStageFilter !== 'ALL' || entries.length === 0) {
        return null;
      }

      const stageCountsByCurrent = PIPELINE_STAGE_SEQUENCE.reduce(
        (acc, stage) => {
          acc[stage] = 0;
          return acc;
        },
        {} as Record<SalesPipelineStage, number>,
      );
      const aggregatedStageValues = PIPELINE_STAGE_SEQUENCE.reduce(
        (acc, stage) => {
          acc[stage] = 0;
          return acc;
        },
        {} as Record<SalesPipelineStage, number>,
      );

      entries.forEach((entry) => {
        const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
        stageCountsByCurrent[stage] = (stageCountsByCurrent[stage] ?? 0) + 1;

        const valueSource = entry.stage_values ?? {};
        PIPELINE_STAGE_SEQUENCE.forEach((stageItem) => {
          const valueKey = PIPELINE_STAGE_VALUE_KEY[stageItem];
          if (!valueKey) {
            return;
          }
          const rawValue = Number((valueSource as SalesPipelineEntry['stage_values'])[valueKey] ?? 0);
          if (Number.isFinite(rawValue) && rawValue !== 0) {
            aggregatedStageValues[stageItem] += rawValue;
          }
        });
      });

      const totalFeatureCount = entries.length;
      const summary = pipelineSummaryRef.current;
      const summaryAllCountRaw = summary?.totalClients;
      const summaryAllCount =
        typeof summaryAllCountRaw === 'number' && Number.isFinite(summaryAllCountRaw) ? summaryAllCountRaw : undefined;
      const summaryTotalValueRaw = summary?.totalValue;
      const summaryTotalValue =
        typeof summaryTotalValueRaw === 'number' && Number.isFinite(summaryTotalValueRaw) ? summaryTotalValueRaw : undefined;
      const globalEntryCount =
        pipelineEntryCountRef.current > 0
          ? pipelineEntryCountRef.current
          : summaryAllCount ?? totalFeatureCount;

      let totalCount = totalFeatureCount;
      if (summaryAllCount && summaryAllCount > 0 && globalEntryCount > 0) {
        if (totalFeatureCount >= globalEntryCount) {
          totalCount = summaryAllCount;
        } else {
          const scaled = Math.round((totalFeatureCount / globalEntryCount) * summaryAllCount);
          totalCount = Math.min(summaryAllCount, Math.max(totalFeatureCount > 0 ? 1 : 0, scaled));
        }
      }

      const stageCountShares = PIPELINE_STAGE_SEQUENCE.map((stage) => ({
        stage,
        share: totalFeatureCount > 0 ? (stageCountsByCurrent[stage] ?? 0) / totalFeatureCount : 0,
      }));
      const provisionalCounts = stageCountShares.map(({ stage, share }) => ({
        stage,
        raw: share * totalCount,
        base: Math.floor(share * totalCount),
      }));
      let assignedCountTotal = provisionalCounts.reduce((sum, item) => sum + item.base, 0);
      let remainder = totalCount - assignedCountTotal;
      const fractions = provisionalCounts
        .map((item) => ({
          stage: item.stage,
          fraction: item.raw - item.base,
        }))
        .sort((a, b) => b.fraction - a.fraction);
      const stageCountsOverride = PIPELINE_STAGE_SEQUENCE.reduce(
        (acc, stage) => {
          const base = provisionalCounts.find((item) => item.stage === stage)?.base ?? 0;
          acc[stage] = base;
          return acc;
        },
        {} as Record<SalesPipelineStage, number>,
      );
      for (let i = 0; i < fractions.length && remainder > 0; i += 1) {
        const targetStage = fractions[i].stage;
        stageCountsOverride[targetStage] += 1;
        remainder -= 1;
      }

      const aggregatedStageValueTotal = PIPELINE_STAGE_SEQUENCE.reduce(
        (sum, stage) => sum + (Number.isFinite(aggregatedStageValues[stage]) ? aggregatedStageValues[stage] : 0),
        0,
      );
      let totalValue = aggregatedStageValueTotal;
      if (summaryTotalValue && summaryTotalValue > 0 && globalEntryCount > 0) {
        if (totalFeatureCount >= globalEntryCount) {
          totalValue = summaryTotalValue;
        } else {
          totalValue = Math.max(0, (totalFeatureCount / globalEntryCount) * summaryTotalValue);
        }
      }

      const stageValueSharesBase = aggregatedStageValueTotal > 0 ? aggregatedStageValueTotal : totalValue;
      const stageValuesOverride = PIPELINE_STAGE_SEQUENCE.reduce(
        (acc, stage) => {
          let share = 0;
          if (stageValueSharesBase > 0 && aggregatedStageValueTotal > 0) {
            share = aggregatedStageValues[stage] / stageValueSharesBase;
          } else {
            const stageCount = stageCountsOverride[stage] ?? 0;
            share = totalCount > 0 ? stageCount / totalCount : 0;
          }
          acc[stage] = share > 0 ? share * totalValue : 0;
          return acc;
        },
        {} as Record<SalesPipelineStage, number>,
      );

      return {
        stageCounts: stageCountsOverride,
        stageValues: stageValuesOverride,
        totalCount,
        totalValue,
      };
    },
    [pipelineStageFilter],
  );

  const pipelineDrawerTableRows = useMemo<PipelineDrawerRow[]>(() => {
    return pipelineDrawerEntries.map((entry, index) => {
      const stageKey = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
      const stageConfig = SALES_PIPELINE_STAGE_COLORS[stageKey] ?? SALES_PIPELINE_STAGE_COLORS.ENQUIRY;
      const countSource = entry.stage_counts ?? {};
      const counts = {
        enquiry: countSource.enquiry ?? 0,
        opportunity: countSource.opportunity ?? 0,
        quotation: countSource.quotation ?? 0,
        sales_order: countSource.sales_order ?? 0,
        invoice: countSource.invoice ?? 0,
      } as SalesPipelineEntry['stage_counts'];
      const valueSource = entry.stage_values ?? {};
      const values = {
        opportunity_value: valueSource.opportunity_value ?? 0,
        quotation_value: valueSource.quotation_value ?? 0,
        sales_order_value: valueSource.sales_order_value ?? 0,
        net_invoice_value: valueSource.net_invoice_value ?? 0,
      } as SalesPipelineEntry['stage_values'];
      const dateSource = entry.stage_dates ?? {};
      const dates = {
        last_enquiry: dateSource.last_enquiry ?? null,
        last_opportunity: dateSource.last_opportunity ?? null,
        last_quotation: dateSource.last_quotation ?? null,
        last_sales_order: dateSource.last_sales_order ?? null,
        last_invoice: dateSource.last_invoice ?? null,
      } as SalesPipelineEntry['stage_dates'];
      const stageCountKey = PIPELINE_STAGE_COUNT_KEY[stageKey];
      const stageValueKey = PIPELINE_STAGE_VALUE_KEY[stageKey];
      const stageDateKey = PIPELINE_STAGE_DATE_KEY[stageKey];
      const stageValueSource = stageValueKey ? values[stageValueKey] : undefined;
      const stageValueRaw = parseChurnAmount(stageValueSource ?? getPipelineStageValue(entry));
      const invoiceValueRaw = parseChurnAmount(values.net_invoice_value ?? 0);
      const currentStageCountRaw = Number(counts[stageCountKey] ?? 0);
      const stageCountLabel = formatPipelineCount(currentStageCountRaw);
      const locationParts = [
        entry.address,
        entry.city,
        entry.state,
        entry.country?.short_name || entry.country?.long_name,
        entry.zipcode,
      ].filter((part) => !!part && String(part).trim().length > 0);

      const stageStats = PIPELINE_STAGE_SEQUENCE.map((stageItem) => {
        const config = SALES_PIPELINE_STAGE_COLORS[stageItem];
        const countKey = PIPELINE_STAGE_COUNT_KEY[stageItem];
        const valueKey = PIPELINE_STAGE_VALUE_KEY[stageItem];
        const dateKey = PIPELINE_STAGE_DATE_KEY[stageItem];
        const rawDate = dates[dateKey];
        const labelText = stageItem.toLowerCase().replace('_', ' ');
        const tooltip = (() => {
          if (!rawDate) return undefined;
          const parsed = new Date(rawDate);
          if (Number.isNaN(parsed.getTime())) {
            return undefined;
          }
          return `Last ${labelText}: ${parsed.toLocaleString()}`;
        })();
        const stageValue = valueKey ? parseChurnAmount(values[valueKey] ?? 0) : 0;
        const shouldShowValue = stageItem !== 'ENQUIRY' && stageItem !== 'OPPORTUNITY' && Number(stageValue) > 0;
        return {
          stage: stageItem,
          label: labelText,
          count: formatPipelineCount(counts[countKey]),
          value: shouldShowValue ? formatCurrency(stageValue) : undefined,
          config,
          isActive: stageItem === stageKey,
          tooltip,
        };
      });

      return {
        key: `${entry.clientid ? String(entry.clientid) : entry.company || 'pipeline-entry'}-${index}`,
        company: entry.company || 'Unknown company',
        location: locationParts.join(', ') || 'Location unavailable',
        city: entry.city || '',
        salesRep: entry.staff_name || '',
        clientId: entry.clientid ? String(entry.clientid) : undefined,
        stageKey,
        stageLabel: stageKey.toLowerCase().replace('_', ' '),
        stageConfig,
        stageValueLabel:
          stageKey === 'ENQUIRY' || stageKey === 'OPPORTUNITY'
            ? stageCountLabel
            : `${stageCountLabel} (${formatCurrency(stageValueRaw)})`,
        stageValueRaw,
        invoiceValueRaw,
        stageCountLabel,
        stageCountRaw: currentStageCountRaw,
        lastUpdated: formatPipelineDate(dates[stageDateKey]),
        stageStats,
      };
    });
  }, [pipelineDrawerEntries]);

  const pipelineDrawerAggregates = useMemo<PipelineDrawerAggregates>(() => {
    const stageCountTotals = PIPELINE_STAGE_SEQUENCE.reduce(
      (acc, stage) => {
        acc[stage] = 0;
        return acc;
      },
      {} as Record<SalesPipelineStage, number>,
    );
    const stageValueTotals = PIPELINE_STAGE_SEQUENCE.reduce(
      (acc, stage) => {
        acc[stage] = 0;
        return acc;
      },
      {} as Record<SalesPipelineStage, number>,
    );

    const uniqueClientIds = new Set<string | number>();

    const totals = pipelineDrawerEntries.reduce(
      (acc, entry) => {
        const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
        const countSource = entry.stage_counts ?? {};
        const counts = {
          enquiry: countSource.enquiry ?? 0,
          opportunity: countSource.opportunity ?? 0,
          quotation: countSource.quotation ?? 0,
          sales_order: countSource.sales_order ?? 0,
          invoice: countSource.invoice ?? 0,
        } as SalesPipelineEntry['stage_counts'];
        const valueSource = entry.stage_values ?? {};
        const values = {
          opportunity_value: valueSource.opportunity_value ?? 0,
          quotation_value: valueSource.quotation_value ?? 0,
          sales_order_value: valueSource.sales_order_value ?? 0,
          net_invoice_value: valueSource.net_invoice_value ?? 0,
        } as SalesPipelineEntry['stage_values'];

        acc.totalValue += getPipelineStageValue(entry);
        acc.totalOpportunities += Number(counts.opportunity ?? 0);

        PIPELINE_STAGE_SEQUENCE.forEach((stageItem) => {
          const countKey = PIPELINE_STAGE_COUNT_KEY[stageItem];
          const rawCount = Number(counts[countKey] ?? 0);
          if (Number.isFinite(rawCount) && rawCount > 0) {
            acc.stageCounts[stageItem] += rawCount;
          } else if (stageItem === stage) {
            acc.stageCounts[stageItem] += 1;
          }

          const valueKey = PIPELINE_STAGE_VALUE_KEY[stageItem];
          if (valueKey) {
            const rawValue = Number(values[valueKey] ?? 0);
            if (Number.isFinite(rawValue) && rawValue !== 0) {
              acc.stageValues[stageItem] += rawValue;
            } else if (stageItem === stage) {
              const fallbackStageValue = Number(getPipelineStageValue(entry));
              if (Number.isFinite(fallbackStageValue) && fallbackStageValue !== 0) {
                acc.stageValues[stageItem] += fallbackStageValue;
              }
            }
          }
        });

        if (entry.clientid) {
          uniqueClientIds.add(entry.clientid);
        } else if (entry.company) {
          uniqueClientIds.add(`${entry.company}-${entry.shipping_id ?? ''}`);
        }

        const rawSkuCount =
          entry.unique_skus ??
          entry.unique_products ??
          (entry as unknown as { uniqueSku?: number }).uniqueSku ??
          (entry as unknown as { uniqueProducts?: number }).uniqueProducts ??
          0;
        const numericSkuCount = Number(rawSkuCount ?? 0);
        if (Number.isFinite(numericSkuCount) && numericSkuCount > 0) {
          acc.uniqueSkuCount += numericSkuCount;
        }

        return acc;
      },
      {
        totalValue: 0,
        totalOpportunities: 0,
        stageCounts: stageCountTotals,
        stageValues: stageValueTotals,
        uniqueClientCount: 0,
        uniqueSkuCount: 0,
      },
    );

    totals.uniqueClientCount = uniqueClientIds.size > 0 ? uniqueClientIds.size : pipelineDrawerEntries.length;

    return totals;
  }, [pipelineDrawerEntries]);

  useEffect(() => {
    if (filters.mapView !== 'sales-pipeline' && pipelineStageFilter !== 'ALL') {
      setPipelineStageFilter('ALL');
    }
  }, [filters.mapView, pipelineStageFilter]);

  useEffect(() => {
    if (filters.mapView !== 'sales-pipeline') {
      setPipelineDrawerEntries([]);
      setPipelineDrawerContext('overall');
      setPipelineDrawerMetricsOverride(null);
    }
  }, [filters.mapView]);

  const previousPipelineStageRef = useRef(pipelineStageFilter);
  useEffect(() => {
    const stageChanged = previousPipelineStageRef.current !== pipelineStageFilter;
    previousPipelineStageRef.current = pipelineStageFilter;

    if (!stageChanged || filters.mapView !== 'sales-pipeline') {
      return;
    }

    setPipelineDrawerEntries([]);
    setPipelineDrawerContext('overall');
    setPipelineDrawerMetricsOverride(null);
    setTableDrawerVisible(false);
  }, [pipelineStageFilter, filters.mapView]);

  const churnedSummary = useMemo<RevenueChurnedSummary>(() => {
    let before = 0;
    let after = 0;
    churnedClients.forEach((row: PopupTableRow) => {
      const invoiceDate = parseInvoiceDate(row.invoice_created_date);
      const metrics = computeOpenSOMetrics(row.open_so, invoiceDate);
      before += metrics.before;
      after += metrics.after;
    });
    const total = before + after;
    const conversionRate = total > 0 ? (after / total) * 100 : null;
    return { before, after, conversionRate };
  }, [churnedClients]);

  useEffect(() => {
    setChurnedPage(1);
  }, [tableSearch, filters.mapView, filters.country, filters.state, filters.city, filters.salesRep, filters.productSearch, filters.startDate, filters.endDate, filters.dateRangePreset, tableRows.length]);

  useEffect(() => {
    if (filters.mapView !== 'revenue') {
      setTopClientsDrawerVisible(false);
      setChurnedDrawerVisible(false);
    }
  }, [filters.mapView]);

  useEffect(() => {
    if (filters.mapView !== 'product') {
      setProductDrawerVisible(false);
      setTopProductsDrawerVisible(false);
      setCityInsightsDrawerVisible(false);
    }
  }, [filters.mapView]);


  useEffect(() => {
    if (topClientRows.length === 0) {
      setTopClientsDrawerVisible(false);
    }
  }, [topClientRows.length]);

  useEffect(() => {
    if (churnedClients.length === 0) {
      setChurnedDrawerVisible(false);
    }
  }, [churnedClients.length]);

  useEffect(() => {
    if (churnedDrawerVisible) {
      setChurnedPage(1);
    }
  }, [churnedDrawerVisible]);

  const churnedTotalPages = Math.max(1, Math.ceil(churnedClients.length / CHURNED_PAGE_SIZE));
  const churnedPageSafe = Math.min(churnedPage, churnedTotalPages);

  useEffect(() => {
    if (churnedPage !== churnedPageSafe) {
      setChurnedPage(churnedPageSafe);
    }
  }, [churnedPage, churnedPageSafe]);

  const paginatedChurnedClients = useMemo(() => {
    const start = (churnedPageSafe - 1) * CHURNED_PAGE_SIZE;
    return churnedClients.slice(start, start + CHURNED_PAGE_SIZE);
  }, [churnedClients, churnedPageSafe]);

  // Create vector sources with clustering
  const [sources] = useState(() => {
    const createClusterSource = () =>
      new Cluster({
        distance: 60,
        source: new VectorSource(),
        minDistance: 30,
        geometryFunction: function (feature: Feature<Geometry>): Point {
          const geometry = feature.getGeometry();
          if (geometry instanceof Point) {
            return geometry;
          }
          // For non-Point geometries, create a new point at [0,0]
          return new Point([0, 0]);
        },
      });

    return {
      client: createClusterSource(),
      warehouse: createClusterSource(),
      vendor: createClusterSource(),
      prospect: createClusterSource(),
      product: createClusterSource(),
    };
  });

  const [supplyHubSources] = useState(() => ({
    warehouses: new VectorSource(),
    vendors: new VectorSource(),
    routes: new VectorSource(),
  }));

  // Create boundary layer source
  const [boundarySource] = useState(() => new VectorSource());

  // Create cluster styles (standard)
  const createClusterStyle = (type: LocationType) => {
    if (type === 'all') return () => undefined;

    const colors: Record<Exclude<LocationType, 'all'>, string> = {
      client: '#4CAF50',
      warehouse: '#F44336',
      vendor: '#FFC107',
      prospect: '#2196F3'
    };

    return (feature: any): Style => {
      const features = feature.get('features');
      const size = features ? features.length : 0;
      
      if (size === 1) {
        return styles[type];
      }

      // Cache the style for this size to prevent recreation
      const cacheKey = `${type}-${size}`;
      if (clusterStyleCache[cacheKey]) {
        return clusterStyleCache[cacheKey];
      }

      const style = new Style({
        image: new Icon({
          src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="50" height="50">
              <circle cx="25" cy="25" r="20" fill="${colors[type]}" stroke="white" stroke-width="2"/>
              <text x="25" y="30" text-anchor="middle" fill="white" font-size="12" font-family="Arial">${size}</text>
            </svg>
          `)}`,
          scale: 0.8
        })
      });

      clusterStyleCache[cacheKey] = style;
      return style;
  };
};

const createProductMarkerStyleFactory = () => {
  const cache: Record<string, Style> = {};

  return (bucket: ProductMarkerBucket): Style => {
    const cacheKey = `${bucket.key}-${bucket.color}`;
    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    const markerSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="26" height="64">
        <defs>
          <filter id="product-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feOffset result="offOut" in="SourceGraphic" dx="0" dy="2" />
            <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
            <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
          </filter>
        </defs>
        <path filter="url(#product-shadow)" stroke="white" stroke-width="8" fill="${bucket.color}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
      </svg>
    `;

    const style = new Style({
      image: new Icon({
        src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markerSvg.trim())}`,
        anchor: [0.5, 1],
        scale: 0.8,
      }),
    });

    cache[cacheKey] = style;
    return style;
  };
};

const getFeatureUniqueProductCount = (feature: FeatureLike | undefined): number => {
  if (!feature || typeof (feature as any).get !== 'function') {
    return 0;
  }

  const direct = (feature as any).get('uniqueProductCount');
  if (Number.isFinite(direct)) {
    return Number(direct);
  }

  const props = (feature as any).get('properties');
  if (props && Number.isFinite(props.uniqueProductCount)) {
    return Number(props.uniqueProductCount);
  }
  if (props && Number.isFinite(props.unique_product_count)) {
    return Number(props.unique_product_count);
  }

  const location =
    (feature as any).get('productLocation') ??
    props?.productLocation ??
    props?.product_location;

  if (location) {
    return computeLocationUniqueProductCount(location as ProductCompanyLocation);
  }

  return 0;
};

const createProductClusterStyle = (
  getBucketForCount: (count: number) => ProductMarkerBucket,
  getMarkerStyle: (bucket: ProductMarkerBucket) => Style,
  activeBucketKey: string | null,
) => {
  const cache: Record<string, Style> = {};
  const isBucketVisible = (bucketKey: string) => !activeBucketKey || activeBucketKey === bucketKey;

  return (feature: any): Style | undefined => {
    const features = feature?.get?.('features');
    const featureArray: FeatureLike[] =
      Array.isArray(features) && features.length > 0 ? (features as FeatureLike[]) : [feature as FeatureLike];
    const size = featureArray.length;

    if (size <= 1) {
      const innerFeature = featureArray[0];
      const uniqueCount = getFeatureUniqueProductCount(innerFeature);
      const bucket = getBucketForCount(uniqueCount);
      if (typeof (innerFeature as any)?.set === 'function') {
        (innerFeature as any).set('uniqueProductCount', uniqueCount);
        (innerFeature as any).set('uniqueProductBucketKey', bucket.key);
        (innerFeature as any).set('uniqueProductBucketColor', bucket.color);
      }
      if (typeof feature?.set === 'function') {
        feature.set('uniqueProductCount', uniqueCount);
        feature.set('uniqueProductBucketKey', bucket.key);
        feature.set('uniqueProductBucketColor', bucket.color);
      }
      if (!isBucketVisible(bucket.key)) {
        return undefined;
      }
      return getMarkerStyle(bucket);
    }

    let matchedCount = 0;
    let totalMatchedUnique = 0;

    featureArray.forEach((inner: FeatureLike) => {
      const innerCount = getFeatureUniqueProductCount(inner);
      const innerBucket = getBucketForCount(innerCount);
      if (typeof (inner as any)?.set === 'function') {
        (inner as any).set('uniqueProductCount', innerCount);
        (inner as any).set('uniqueProductBucketKey', innerBucket.key);
        (inner as any).set('uniqueProductBucketColor', innerBucket.color);
      }
      if (isBucketVisible(innerBucket.key)) {
        matchedCount += 1;
        totalMatchedUnique += innerCount;
      }
    });

    if (matchedCount === 0) {
      return undefined;
    }

    const averageUniqueCount = totalMatchedUnique / matchedCount;
    const bucket = getBucketForCount(averageUniqueCount);
    const visibleCount = activeBucketKey ? matchedCount : size;
    const cacheKey = `${bucket.key}-${bucket.color}-${visibleCount}-${activeBucketKey ?? 'all'}`;

    if (typeof feature?.set === 'function') {
      feature.set('uniqueProductCount', averageUniqueCount);
      feature.set('uniqueProductBucketKey', bucket.key);
      feature.set('uniqueProductBucketColor', bucket.color);
      feature.set('uniqueProductMatchCount', matchedCount);
    }

    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    const gradientStart = lightenHexColor(bucket.color, 30);
    const gradientEnd = darkenHexColor(bucket.color, 18);
    const strokeColor = lightenHexColor(bucket.color, 50);

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52" width="52" height="52">
        <defs>
          <linearGradient id="product-cluster-fill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="${gradientStart}"/>
            <stop offset="100%" stop-color="${gradientEnd}"/>
          </linearGradient>
        </defs>
        <circle cx="26" cy="26" r="22" fill="url(#product-cluster-fill)" stroke="${strokeColor}" stroke-width="2"/>
        <text x="26" y="32" text-anchor="middle" fill="#ffffff" font-size="14" font-family="Arial" font-weight="700">${visibleCount}</text>
      </svg>
    `;

    const style = new Style({
      image: new Icon({
        src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`,
        scale: 0.8,
      }),
    });

    cache[cacheKey] = style;
    return style;
  };
};

// Revenue map style: pin marker colored by tier + right-side amount bubble ($, K/M)
const createRevenueStyle = () => {
    // Caches
    const styleCache: Record<string, Style[]> = {};
    const pinCache: Record<string, Style> = {};

    const parseAmount = (v: any): number => {
      if (v === undefined || v === null || v === '') return 0;
      if (typeof v === 'number') return v;
      const s = String(v);
      if (/m$/i.test(s)) return parseFloat(s) * 1_000_000;
      if (/k$/i.test(s)) return parseFloat(s) * 1_000;
      const num = parseFloat(s.replace(/[^0-9.-]+/g, ''));
      return Number.isFinite(num) ? num : 0;
    };

    const formatShortUSD = (n: number) => {
      if (n >= 1_000_000) return `$${(n/1_000_000).toFixed(1)}M`;
      if (n >= 1_000) return `$${(n/1_000).toFixed(1)}K`;
      return `$${Math.round(n).toString()}`;
    };

    const getTierColor = (n: number) => {
      if (n > 1_000_000) return '#2e7d32'; // High
      if (n >= 500_000) return '#1a73e8';  // Good
      return '#f29900';                    // Emerging
    };

    const getPinStyle = (hex: string) => {
      if (pinCache[hex]) return pinCache[hex];
      const markerSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="24" height="60">
          <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feOffset result="offOut" in="SourceGraphic" dx="0" dy="2" />
              <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
              <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
            </filter>
          </defs>
          <path filter="url(#shadow)" stroke="white" stroke-width="8" fill="${hex}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
        </svg>
      `;
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markerSvg.trim())}`;
      const st = new Style({ image: new Icon({ src: url, anchor: [0.5, 1], scale: 0.8 }) });
      pinCache[hex] = st;
      return st;
    };

    return (feature: any): Style | Style[] => {
      // Sum amount in cluster or read single
      const feats = feature.get('features');
      let amount = 0;
      if (feats && feats.length > 0) {
        for (const f of feats) {
          const p = f.get('properties') || {};
          amount += parseAmount(p.total_amount);
        }
      } else {
        const p = feature.get('properties') || {};
        amount = parseAmount(p.total_amount);
      }

      const label = formatShortUSD(amount);
      const color = getTierColor(amount);
      const cacheKey = `${label}_${color}`;
      if (styleCache[cacheKey]) return styleCache[cacheKey];

      const markerStyle = getPinStyle(color);
      // Price label bubble above the marker (lifted so it doesn't occlude the pin)
      const textStyle = new Style({
        text: new Text({
          text: label,
          font: '600 12px Inter, Arial, sans-serif',
          fill: new Fill({ color: '#111' }),
          backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          backgroundStroke: new Stroke({ color: '#d0d7de', width: 1 }),
          padding: [3, 6, 3, 6],
          offsetX: 0,
          offsetY: -48,
        })
      });

      // Small arrow pointer under the bubble (two layers: border then fill) using unicode triangle
      // Arrow should touch the marker top (pin height ~60 * 0.8 = 48px above anchor)
      // Place the bottom of the triangle at ~-48 so it visually touches the pin
      const arrowBorder = new Style({
        text: new Text({
          text: '\u25BC',
          font: '700 16px Inter, Arial, sans-serif',
          fill: new Fill({ color: '#d0d7de' }),
          offsetX: 0,
          offsetY: -48,
        })
      });
      const arrowFill = new Style({
        text: new Text({
          text: '\u25BC',
          font: '700 14px Inter, Arial, sans-serif',
          fill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          offsetX: 0,
          offsetY: -30,
          textAlign: 'center',
          textBaseline: 'bottom'
        })
      });

      const arr: Style[] = [markerStyle, arrowBorder, arrowFill, textStyle];
      styleCache[cacheKey] = arr;
      return arr;
    };
  };

const createFrontsiteStyle = () => {
  const styleCache: Record<string, Style[]> = {};
  const markerSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="24" height="60">
      <defs>
        <filter id="frontsite-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feOffset result="offOut" in="SourceGraphic" dx="0" dy="2" />
          <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
          <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
      </defs>
      <path filter="url(#frontsite-shadow)" stroke="white" stroke-width="8" fill="#2e7d32" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
    </svg>
  `;
  const pinStyle = new Style({
    image: new Icon({
      src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markerSvg.trim())}`,
      anchor: [0.5, 1],
      scale: 0.8,
    }),
  });

  const parseAmount = (value: unknown) => {
    if (value === undefined || value === null || value === '') {
      return 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    const stringValue = String(value);
    if (/m$/i.test(stringValue)) {
      return parseFloat(stringValue) * 1_000_000;
    }
    if (/k$/i.test(stringValue)) {
      return parseFloat(stringValue) * 1_000;
    }
    const numeric = parseFloat(stringValue.replace(/[^0-9.-]+/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (feature: any): Style | Style[] => {
    const clusteredFeatures = feature.get('features');
    let amount = 0;

    if (clusteredFeatures && clusteredFeatures.length > 0) {
      clusteredFeatures.forEach((f: FeatureLike) => {
        const props = f.get('properties') || {};
        amount += parseAmount(props.total_amount);
      });
    } else {
      const props = feature.get('properties') || {};
      amount = parseAmount(props.total_amount);
    }

    const label = formatter.format(amount);
    const cacheKey = label;
    if (styleCache[cacheKey]) {
      return styleCache[cacheKey];
    }

    const textStyle = new Style({
      text: new Text({
        text: label,
        font: '600 12px "Inter","Arial",sans-serif',
        fill: new Fill({ color: '#0f172a' }),
        backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
        backgroundStroke: new Stroke({ color: '#d0d7de', width: 1 }),
        padding: [3, 8, 3, 8],
        offsetY: -48,
        textAlign: 'center',
      }),
    });

    const arrowBorder = new Style({
      text: new Text({
        text: '\u25BC',
        font: '700 16px "Inter","Arial",sans-serif',
        fill: new Fill({ color: '#d0d7de' }),
        offsetY: -48,
      }),
    });

    const arrowFill = new Style({
      text: new Text({
        text: '\u25BC',
        font: '700 14px "Inter","Arial",sans-serif',
        fill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
        offsetY: -30,
      }),
    });

    const styles = [pinStyle, arrowBorder, arrowFill, textStyle];
    styleCache[cacheKey] = styles;
    return styles;
  };
};

  const createMarkerStyle = (type: LocationType): Style => {
    if (type === 'all') {
      return new Style({}); // Return empty style instead of null
    }

    const colors: Record<Exclude<LocationType, 'all'>, string> = {
      client: '#4CAF50',
      warehouse: '#F44336',
      vendor: '#FFC107',
      prospect: '#2196F3'
    };

    const markerSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="24" height="60">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feOffset result="offOut" in="SourceGraphic" dx="0" dy="2" />
            <feGaussianBlur result="blurOut" in="offOut" stdDeviation="2" />
            <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
          </filter>
        </defs>
        <path filter="url(#shadow)" stroke="white" stroke-width="8" fill="${colors[type]}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
      </svg>
    `;

    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markerSvg.trim())}`;

    return new Style({
      image: new Icon({
        src: url,
        anchor: [0.5, 1],
        scale: 0.8
      })
    });
  };

  const {
    data: productLocationsResponse = { total_companies: 0, locations: [] },
    isLoading: isLoadingProductLocations,
  } = useQuery<ProductCompanyLocationsResponse>({
    queryKey: [
      'product-company-locations',
      filters.country,
      filters.state,
      filters.city,
      filters.productSearch,
      filters.searchTerm,
      filters.startDate,
      filters.endDate,
      filters.dateRangePreset,
    ],
    queryFn: () =>
      fetchProductCompanyLocations({
        country: filters.country || undefined,
        state: filters.state || undefined,
        city: filters.city || undefined,
        product: filters.productSearch || undefined,
        search: filters.searchTerm || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }),
    enabled: showMap && filters.mapView === 'product' && productMapRequested,
    staleTime: 300000,
  });

  const {
    data: supplyHubResponse,
    isLoading: isLoadingSupplyHub,
  } = useQuery<SupplyHubMapResponse>({
    queryKey: [
      'supply-hub-map',
      filters.country,
      filters.state,
      filters.salesRep,
      filters.startDate,
      filters.endDate,
    ],
    queryFn: () =>
      fetchSupplyHubMap(
        filters.country || undefined,
        filters.state || undefined,
        filters.salesRep || undefined,
        filters.startDate || undefined,
        filters.endDate || undefined,
      ),
    enabled: showMap && filters.mapView === 'supply-hub',
    staleTime: 300000,
  });

  const supplyHubWarehouses = supplyHubResponse?.warehouses ?? [];

  const supplyHubVendorGroups = useMemo<SupplyHubVendorGroup[]>(() => {
    if (!Array.isArray(supplyHubWarehouses)) {
      return [];
    }

    const groups: SupplyHubVendorGroup[] = [];

    supplyHubWarehouses.forEach((warehouse) => {
      const purchaseOrders = Array.isArray(warehouse.purchase_orders) ? warehouse.purchase_orders : [];
      if (purchaseOrders.length === 0) {
        return;
      }

      const vendorMap = new globalThis.Map<string, SupplyHubVendorGroup>();

      purchaseOrders.forEach((order) => {
        const location = order?.vendor_location;
        if (
          !location ||
          !isFiniteNumber(location.latitude) ||
          !isFiniteNumber(location.longitude)
        ) {
          return;
        }

        const warehouseId = warehouse.warehouse_id ?? warehouse.warehouse_name ?? 'warehouse';
        const vendorId = order.vendorid ?? order.vendor_name ?? 'vendor';
        const key = `${warehouseId}-${vendorId}-${location.latitude}-${location.longitude}`;

        if (!vendorMap.has(key)) {
          vendorMap.set(key, {
            key,
            vendorId: order.vendorid ?? '',
            vendorName: order.vendor_name ?? 'Unknown Vendor',
            location,
            warehouse,
            purchaseOrders: [],
            totalOrders: 0,
            totalValue: 0,
            shippingSummary: {
              air: { count: 0, orders: [], totalValue: 0 },
              sea: { count: 0, orders: [], totalValue: 0 },
              truck: { count: 0, orders: [], totalValue: 0 },
              pickup: { count: 0, orders: [], totalValue: 0 },
              other: { count: 0, orders: [], totalValue: 0 },
            },
          });
        }

        const group = vendorMap.get(key)!;
        group.purchaseOrders.push(order);
        group.totalOrders += 1;
        const orderValue = Number(order.total) || 0;
        group.totalValue += orderValue;

        const shippingMode = resolveSupplyHubShippingMode(order.ship_method || order.ship_term);
        const summary = group.shippingSummary[shippingMode];
        summary.count += 1;
        summary.orders.push(order);
        summary.totalValue += orderValue;
      });

      vendorMap.forEach((group) => {
        groups.push(group);
      });
    });

    return groups;
  }, [supplyHubWarehouses]);

  const supplyHubModeFilteredVendorGroups = useMemo<SupplyHubVendorGroup[]>(() => {
    if (supplyHubModeFilter === 'all') {
      return supplyHubVendorGroups;
    }

    return supplyHubVendorGroups
      .map((group) => {
        const summary = group.shippingSummary[supplyHubModeFilter];
        if (!summary || summary.count === 0) {
          return null;
        }

        const sanitizedSummary: SupplyHubShippingSummary = {
          air: { count: 0, orders: [], totalValue: 0 },
          sea: { count: 0, orders: [], totalValue: 0 },
          truck: { count: 0, orders: [], totalValue: 0 },
          pickup: { count: 0, orders: [], totalValue: 0 },
          other: { count: 0, orders: [], totalValue: 0 },
        };

        sanitizedSummary[supplyHubModeFilter] = {
          count: summary.count,
          orders: summary.orders,
          totalValue: summary.totalValue,
        };

        return {
          ...group,
          purchaseOrders: summary.orders,
          totalOrders: summary.count,
          totalValue: summary.totalValue,
          shippingSummary: sanitizedSummary,
        };
      })
      .filter((group): group is SupplyHubVendorGroup => group !== null);
  }, [supplyHubVendorGroups, supplyHubModeFilter]);

  const supplyHubDisplayVendorGroups = useMemo<SupplyHubVendorGroup[]>(() => {
    return supplyHubModeFilter === 'all' ? supplyHubVendorGroups : supplyHubModeFilteredVendorGroups;
  }, [supplyHubModeFilter, supplyHubVendorGroups, supplyHubModeFilteredVendorGroups]);

  const supplyHubDisplayVendorGroupsByWarehouse = useMemo(() => {
    const mapping = new globalThis.Map<string, SupplyHubVendorGroup[]>();
    supplyHubDisplayVendorGroups.forEach((group) => {
      const key = buildSupplyHubWarehouseKey(group.warehouse);
      if (!mapping.has(key)) {
        mapping.set(key, []);
      }
      mapping.get(key)!.push(group);
    });
    return mapping;
  }, [supplyHubDisplayVendorGroups]);

  const supplyHubDisplayWarehouses = useMemo(() => {
    if (supplyHubModeFilter === 'all') {
      return supplyHubWarehouses
        .map((warehouse) => ({
          warehouse,
          totalOrders: Array.isArray(warehouse.purchase_orders) ? warehouse.purchase_orders.length : 0,
        }))
        .filter((entry) => entry.totalOrders > 0);
    }

    const warehouseMap = new globalThis.Map<string, { warehouse: SupplyHubWarehouse; totalOrders: number }>();
    supplyHubDisplayVendorGroups.forEach((group) => {
      const key = buildSupplyHubWarehouseKey(group.warehouse);
      const existing = warehouseMap.get(key);
      const orderCount = group.purchaseOrders.length;
      if (existing) {
        existing.totalOrders += orderCount;
      } else {
        warehouseMap.set(key, { warehouse: group.warehouse, totalOrders: orderCount });
      }
    });
    return Array.from(warehouseMap.values());
  }, [supplyHubModeFilter, supplyHubWarehouses, supplyHubDisplayVendorGroups]);

  const supplyHubDisplayTotalPurchaseOrders = useMemo(
    () => supplyHubDisplayVendorGroups.reduce((acc, group) => acc + group.purchaseOrders.length, 0),
    [supplyHubDisplayVendorGroups],
  );

  const supplyHubShippingTotals = useMemo(() => {
    const totals: Record<SupplyHubShippingMode, { count: number; value: number }> = {
      air: { count: 0, value: 0 },
      sea: { count: 0, value: 0 },
      truck: { count: 0, value: 0 },
      pickup: { count: 0, value: 0 },
      other: { count: 0, value: 0 },
    };

    supplyHubVendorGroups.forEach((group) => {
      (Object.keys(group.shippingSummary) as SupplyHubShippingMode[]).forEach((mode) => {
        const summary = group.shippingSummary[mode];
        totals[mode].count += summary.count;
        totals[mode].value += summary.totalValue;
      });
    });

    return totals;
  }, [supplyHubVendorGroups]);

  const supplyHubDrawerTitle = useMemo(() => {
    if (!supplyHubDrawerContext) {
      return 'Supply Hub Details';
    }

    if (supplyHubDrawerContext.type === 'warehouse') {
      const warehouse = supplyHubDrawerContext.warehouse;
      const label = [warehouse.warehouse_name, warehouse.city, warehouse.state]
        .filter((part) => (part || '').toString().trim().length > 0)
        .join(', ');
      return label.length ? `Warehouse - ${label}` : 'Warehouse Details';
    }

    if (supplyHubDrawerContext.type === 'vendor') {
      return `Vendor - ${supplyHubDrawerContext.vendorGroup.vendorName}`;
    }

    const config = SUPPLY_HUB_SHIPPING_MODE_CONFIG[supplyHubDrawerContext.shippingMode];
    return `${config.label} Route - ${supplyHubDrawerContext.vendorGroup.vendorName}`;
  }, [supplyHubDrawerContext]);

  const buildCurvedRouteCoordinates = useCallback(
    (start: Coordinate, end: Coordinate): Coordinate[] => {
      const mid: Coordinate = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
      const dx = end[0] - start[0];
      const dy = end[1] - start[1];
      const length = Math.hypot(dx, dy);
      if (length === 0) {
        return [start, end];
      }

      const normX = -dy / length;
      const normY = dx / length;
      const curvature = Math.min(length * 0.25, 500000);
      const direction = dx >= 0 ? 1 : -1;
      const controlPoint: Coordinate = [
        mid[0] + normX * curvature * direction,
        mid[1] + normY * curvature * direction,
      ];

      const segments = 32;
      const points: Coordinate[] = [];
      for (let i = 0; i <= segments; i += 1) {
        const t = i / segments;
        const oneMinusT = 1 - t;
        const x = oneMinusT * oneMinusT * start[0]
          + 2 * oneMinusT * t * controlPoint[0]
          + t * t * end[0];
        const y = oneMinusT * oneMinusT * start[1]
          + 2 * oneMinusT * t * controlPoint[1]
          + t * t * end[1];
        points.push([x, y]);
      }

      return points;
    },
    [],
  );

  const getSupplyHubWarehouseStyles = (warehouse: SupplyHubWarehouse, totalOrders: number): Style[] => {
    const cacheKey = `${warehouse.warehouse_id ?? warehouse.warehouse_name ?? 'warehouse'}-${totalOrders}`;
    const cache = supplyHubStyleCache.current.warehouse;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const name = warehouse.warehouse_name || 'Warehouse';
    const styles: Style[] = [
      new Style({
        image: new CircleStyle({
          radius: 10,
          fill: new Fill({ color: '#dc2626' }),
          stroke: new Stroke({ color: '#ffffff', width: 2 }),
        }),
      }),
      new Style({
        text: new Text({
          text: `${name}${totalOrders > 0 ? ` (${totalOrders})` : ''}`,
          font: '600 12px "Inter","Arial",sans-serif',
          fill: new Fill({ color: '#0f172a' }),
          backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          backgroundStroke: new Stroke({ color: 'rgba(15,23,42,0.25)', width: 1 }),
          padding: [3, 8, 3, 8],
          offsetY: -24,
        }),
      }),
    ];

    cache.set(cacheKey, styles);
    return styles;
  };

  const getSupplyHubRouteIcon = useCallback((mode: SupplyHubShippingMode): Icon | null => {
    const cacheKey = mode;
    const iconCache = supplyHubStyleCache.current.routeIcon;
    if (iconCache.has(cacheKey)) {
      return iconCache.get(cacheKey)!;
    }

    if (typeof document === 'undefined') {
      return null;
    }

    const glyph = SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode]?.glyph || '';
    const color = SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode]?.color || '#0f172a';

    const size = 48;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    ctx.clearRect(0, 0, size, size);
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, (size / 2) - 4, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.restore();

    ctx.font = '700 26px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji","Inter","Arial",sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#0f172a';
    ctx.fillText(glyph, size / 2, size / 2 + 2);

    const img = new Image();
    img.src = canvas.toDataURL('image/png');

    const icon = new Icon({
      img,
      anchor: [0.5, 0.5],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
    });

    iconCache.set(cacheKey, icon);
    return icon;
  }, []);

  const getSupplyHubVendorStyles = (group: SupplyHubVendorGroup): Style[] => {
    const dominantMode = (Object.entries(group.shippingSummary) as [SupplyHubShippingMode, { count: number }][])
      .reduce<SupplyHubShippingMode>(
        (topMode, [mode, summary]) =>
          summary.count > group.shippingSummary[topMode].count ? mode : topMode,
        'other',
      );

    const topMode = dominantMode;
    const config = SUPPLY_HUB_SHIPPING_MODE_CONFIG[topMode];
    const cacheKey = `${group.key}-${group.totalOrders}-${topMode}`;
    const cache = supplyHubStyleCache.current.vendor;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const label = `${group.vendorName}${group.totalOrders > 0 ? ` (${group.totalOrders})` : ''}`;

    const styles: Style[] = [
      new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: config.color }),
          stroke: new Stroke({ color: '#0f172a', width: 2 }),
        }),
      }),
      new Style({
        text: new Text({
          text: label,
          font: '600 11px "Inter","Arial",sans-serif',
          fill: new Fill({ color: '#0f172a' }),
          backgroundFill: new Fill({ color: 'rgba(255,255,255,0.95)' }),
          backgroundStroke: new Stroke({ color: 'rgba(15,23,42,0.2)', width: 1 }),
          padding: [2, 6, 2, 6],
          offsetY: -20,
        }),
      }),
    ];

    cache.set(cacheKey, styles);
    return styles;
  };

  const getSupplyHubRouteStyles = (mode: SupplyHubShippingMode, orderCount: number): Style[] => {
    const config = SUPPLY_HUB_SHIPPING_MODE_CONFIG[mode];
    const cacheKey = `${mode}-${orderCount}`;
    const cache = supplyHubStyleCache.current.route;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const width = Math.min(8, 2 + orderCount);
    const lineDash = config.lineDash;
    const glyph = config.glyph || '';
    const iconImage = getSupplyHubRouteIcon(mode);

    const styles: Style[] = [
      new Style({
        stroke: new Stroke({
          color: 'rgba(15,23,42,0.15)',
          width: width + 2,
          lineCap: 'round',
        }),
      }),
      new Style({
        stroke: new Stroke({
          color: config.color,
          width,
          lineCap: 'round',
          lineDash,
        }),
      }),
      new Style({
        geometry: (feature) => {
          const geometry = feature.getGeometry();
          if (geometry instanceof LineString) {
            return new Point(geometry.getCoordinateAt(0.5));
          }
          return undefined;
        },
        image: iconImage ?? new CircleStyle({
          radius: 12,
          fill: new Fill({ color: '#ffffff' }),
          stroke: new Stroke({ color: config.color, width: 3 }),
        }),
      }),
    ];

    if (!iconImage && glyph) {
      styles.push(
        new Style({
          geometry: (feature) => {
            const geometry = feature.getGeometry();
            if (geometry instanceof LineString) {
              return new Point(geometry.getCoordinateAt(0.5));
            }
            return undefined;
          },
          text: new Text({
            text: glyph,
            font: '700 18px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji","Inter","Arial",sans-serif',
            fill: new Fill({ color: '#0f172a' }),
            textAlign: 'center',
            offsetY: -1,
          }),
        }),
      );
    }


    cache.set(cacheKey, styles);
    return styles;
  };

  const productLocations = productLocationsResponse?.locations ?? [];
  const productAggregates = useMemo<ProductAggregate[]>(() => buildProductAggregates(productLocations), [productLocations]);
  const productCityInsights = useMemo<CityProductInsight[]>(() => buildProductCityInsights(productLocations), [productLocations]);
  const [activeProductBucketKey, setActiveProductBucketKey] = useState<string | null>(null);

  const handleProductBucketToggle = useCallback((bucketKey: string | null) => {
    if (bucketKey === null) {
      setActiveProductBucketKey(null);
      return;
    }
    setActiveProductBucketKey((current) => (current === bucketKey ? null : bucketKey));
  }, []);

  const productLocationStats = useMemo<{ key: string; uniqueProductCount: number }[]>(
    () => {
      if (!Array.isArray(productLocations) || productLocations.length === 0) {
        return [];
      }

      const uniqueLocationMap: Record<string, { key: string; uniqueProductCount: number }> = {};
      productLocations.forEach((location) => {
        const key = buildProductLocationKey(location);
        uniqueLocationMap[key] = {
          key,
          uniqueProductCount: computeLocationUniqueProductCount(location),
        };
      });

      return Object.values(uniqueLocationMap);
    },
    [productLocations],
  );
  const productMarkerBuckets = useMemo<ProductMarkerBucket[]>(
    () => deriveProductMarkerBuckets(productLocationStats.map((stat) => stat.uniqueProductCount)),
    [productLocationStats],
  );
  const productSummary = useMemo(() => {
    const uniqueLocationKeys = new Set(productLocationStats.map((stat) => stat.key));
    const totalQuantity = productAggregates.reduce((sum, item) => sum + item.totalQuantity, 0);
    const totalAmount = productAggregates.reduce((sum, item) => sum + item.totalAmount, 0);

    return {
      totalCompanies: uniqueLocationKeys.size,
      uniqueProducts: productAggregates.length,
      totalQuantity,
      totalAmount,
    };
  }, [productLocationStats, productAggregates]);

  const productTopProducts = useMemo<ProductAggregate[]>(() => {
    if (productAggregates.length === 0) return [];
    return [...productAggregates]
      .sort((a, b) => {
        if (b.totalQuantity === a.totalQuantity) {
          return b.totalAmount - a.totalAmount;
        }
        return b.totalQuantity - a.totalQuantity;
      })
      .slice(0, 10);
  }, [productAggregates]);

  // Cache for cluster styles
  const clusterStyleCache: Record<string, Style> = {};

  const [styles] = useState(() => ({
    client: createMarkerStyle('client'),
    warehouse: createMarkerStyle('warehouse'),
    vendor: createMarkerStyle('vendor'),
    prospect: createMarkerStyle('prospect')
  }));

  const productMarkerStyleFactory = useMemo(() => createProductMarkerStyleFactory(), []);
  const defaultClientClusterStyle = useMemo(() => createClusterStyle('client'), [styles]);

  const initialProductClusterStyle = useMemo(
    () =>
      createProductClusterStyle(
        (count: number) => selectProductMarkerBucket(DEFAULT_PRODUCT_BUCKETS, count),
        productMarkerStyleFactory,
        null,
      ),
    [productMarkerStyleFactory],
  );

  const resolvedProductBuckets = useMemo(
    () => (productMarkerBuckets.length > 0 ? productMarkerBuckets : DEFAULT_PRODUCT_BUCKETS),
    [productMarkerBuckets],
  );

  const productBucketSummaries = useMemo<ProductLegendBucketSummary[]>(() => {
    if (resolvedProductBuckets.length === 0) {
      return [];
    }

    const counts: Record<string, number> = {};
    resolvedProductBuckets.forEach((bucket) => {
      counts[bucket.key] = 0;
    });

    productLocationStats.forEach((stat) => {
      const bucket = selectProductMarkerBucket(resolvedProductBuckets, stat.uniqueProductCount);
      counts[bucket.key] = (counts[bucket.key] ?? 0) + 1;
    });

    return resolvedProductBuckets.map((bucket) => ({
      bucket,
      companyCount: counts[bucket.key] ?? 0,
    }));
  }, [productLocationStats, resolvedProductBuckets]);

  const productClusterStyle = useMemo(
    () =>
      createProductClusterStyle(
        (count: number) => selectProductMarkerBucket(resolvedProductBuckets, count),
        productMarkerStyleFactory,
        activeProductBucketKey,
      ),
    [resolvedProductBuckets, productMarkerStyleFactory, activeProductBucketKey],
  );

  useEffect(() => {
    if (filters.mapView !== 'product') {
      setActiveProductBucketKey(null);
    }
  }, [filters.mapView]);

  useEffect(() => {
    if (activeProductBucketKey && !resolvedProductBuckets.some((bucket) => bucket.key === activeProductBucketKey)) {
      setActiveProductBucketKey(null);
    }
  }, [resolvedProductBuckets, activeProductBucketKey]);

  // Create boundary layer style
  const boundaryStyle = new Style({
    stroke: new Stroke({
      color: '#f80000ff',
      width: 3,
      lineDash: [10, 5]
    }),
    fill: new Fill({
      color: 'rgba(255, 107, 53, 0.1)'
    })
  });

  // Create boundary layer
  const [boundaryLayer] = useState(() => new VectorLayer({
    source: boundarySource,
    style: boundaryStyle,
    zIndex: 0, // Behind all other layers
  }));

  // Create vector layers with cluster styling
  const [layers] = useState(() => ({
    client: new VectorLayer({
      source: sources.client,
      style: createClusterStyle('client'),
      zIndex: 4,
    }),
    warehouse: new VectorLayer({
      source: sources.warehouse,
      style: createClusterStyle('warehouse'),
      zIndex: 3,
    }),
    vendor: new VectorLayer({
      source: sources.vendor,
      style: createClusterStyle('vendor'),
      zIndex: 2,
    }),
    prospect: new VectorLayer({
      source: sources.prospect,
      style: createClusterStyle('prospect'),
      zIndex: 1,
    }),
    product: new VectorLayer({
      source: sources.product,
      style: initialProductClusterStyle,
      zIndex: 5,
    }),
    supplyHubRoutes: new VectorLayer({
      source: supplyHubSources.routes,
      visible: false,
      zIndex: 1,
    }),
    supplyHubVendors: new VectorLayer({
      source: supplyHubSources.vendors,
      visible: false,
      zIndex: 3,
    }),
    supplyHubWarehouses: new VectorLayer({
      source: supplyHubSources.warehouses,
      visible: false,
      zIndex: 4,
    }),
  }));


  useEffect(() => {
    if (!layers?.client) return;
    if (filters.mapView === 'sales-pipeline') {
      layers.client.setStyle(pipelineClusterStyle as any);
    } else {
      layers.client.setStyle(defaultClientClusterStyle);
    }
    if (layers.product) {
      layers.product.setStyle(productClusterStyle);
      layers.product.changed();
    }
  }, [filters.mapView, layers.client, layers.product, pipelineClusterStyle, defaultClientClusterStyle, productClusterStyle]);

  // Increase stale time to reduce refetches
  const { data: locationsResponse = { locations: [], total_amount: 0 }, isLoading: isLoadingLocations } = useQuery<LocationResponse>({
    queryKey: ['locations', filters.productSearch, filters.country, filters.state, filters.salesRep, filters.city, filters.location, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchLocations(filters.productSearch, filters.country, filters.state, filters.salesRep, filters.city, filters.location, filters.startDate, filters.endDate),
    enabled: showMap && filters.mapView !== 'sales-pipeline' && filters.mapView !== 'product',
    staleTime: 300000 // Cache for 5 minutes
  });

  const { data: warehouses = [], isLoading: isLoadingWarehouses } = useQuery({
    queryKey: ['warehouses', filters.country, filters.state, filters.mapView],
    queryFn: () => fetchWarehouses(filters.country, filters.state),
    enabled: showMap && filters.mapView === 'standard',
    staleTime: 300000
  });

  const { data: vendors = [], isLoading: isLoadingVendors } = useQuery({
    queryKey: ['vendors', filters.productSearch, filters.country, filters.state, filters.city, filters.mapView, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchVendors(filters.productSearch, filters.country, filters.state, filters.city, filters.startDate, filters.endDate),
    enabled: showMap && filters.mapView === 'standard',
    staleTime: 300000
  });

  const { data: prospects = [], isLoading: isLoadingProspects } = useQuery({
    queryKey: ['prospects', filters.country, filters.state, filters.salesRep, filters.city, filters.mapView, filters.startDate, filters.endDate, filters.productSearch, filters.dateRangePreset],
    queryFn: () => fetchProspects(filters.country, filters.state, filters.salesRep, filters.city, filters.productSearch, filters.startDate, filters.endDate),
    enabled: showMap && filters.mapView === 'standard',
    staleTime: 300000
  });

  const tableRowsByType = useMemo<Record<PopupTableRow['type'], PopupTableRow[]>>(() => ({
    client: locationsResponse.locations.map((location) => ({
      type: 'client',
      name: location.company || '',
      userid: location.userid,
      location_id: location.userid,
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || '',
      country: location.country?.short_name || '',
      staff: location.staff_name || '',
      orders: location.order_count ?? undefined,
      products: location.unique_products ?? undefined,
      open_so: location.open_so ?? '',
      target: (location as any).target ?? undefined,
      target_year: (location as any).target_year ?? undefined,
      target_achievement: (location as any).target_achievement ?? undefined,
      total_amount: location.total_amount ?? undefined,
    })),
    warehouse: warehouses.map((warehouse) => ({
      type: 'warehouse',
      name: warehouse.name || '',
      address: warehouse.address || '',
      city: warehouse.city || '',
      state: warehouse.state || '',
      zip: warehouse.zip || '',
      country: warehouse.country?.short_name || '',
    })),
    vendor: vendors.map((vendor) => ({
      type: 'vendor',
      name: vendor.company || '',
      userid: vendor.userid,
      location_id: vendor.id ? String(vendor.id) : vendor.userid,
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      zip: vendor.zip || '',
      country: vendor.country?.short_name || '',
      email: vendor.email_1 || '',
      phone: vendor.phonenumber || '',
    })),
    prospect: prospects.map((prospect) => ({
      type: 'prospect',
      name: prospect.company || '',
      userid: prospect.userid,
      location_id: (prospect as any).id ? String((prospect as any).id) : prospect.userid,
      address: prospect.address || '',
      city: prospect.city || '',
      state: prospect.state || '',
      zip: prospect.zip || '',
      country: prospect.country?.short_name || '',
      staff: prospect.staff_name || '',
      quotation_count: prospect.quotation_count ?? undefined,
      enquiry_count: prospect.enquiry_count ?? undefined,
      target: prospect.target ?? undefined,
      target_year: prospect.target_year ?? undefined,
    })),
    product: [],
  }), [locationsResponse.locations, warehouses, vendors, prospects]);

  const handleDrawerTabChange = useCallback((type: PopupTableRow['type']) => {
    setDrawerActiveTab(type);
    setUseTabData(true);
    const preferredRows = drawerRowsByType[type] && drawerRowsByType[type].length > 0
      ? drawerRowsByType[type]
      : (tableRowsByType[type] || []);
    setTableRows(preferredRows);
  }, [drawerRowsByType, tableRowsByType]);

  useEffect(() => {
    if (!useTabData) return;
    const preferredRows = drawerRowsByType[drawerActiveTab] && drawerRowsByType[drawerActiveTab].length > 0
      ? drawerRowsByType[drawerActiveTab]
      : (tableRowsByType[drawerActiveTab] || []);
    setTableRows(preferredRows);
  }, [useTabData, tableRowsByType, drawerRowsByType, drawerActiveTab]);

  const { data: frontsiteData, isLoading: isLoadingFrontsite } = useQuery<FrontsiteClientsResponse>({
    queryKey: [
      'frontsite-clients',
      filters.country,
      filters.state,
      filters.city,
      filters.startDate,
      filters.endDate,
      filters.searchTerm,
      filters.productSearch,
      filters.mapView,
    ],
    queryFn: () =>
      fetchFrontsiteClients({
        country: filters.country,
        state: filters.state,
        city: filters.city,
        startDate: filters.startDate,
        endDate: filters.endDate,
        searchTerm: filters.searchTerm,
        productSearch: filters.productSearch,
      }),
    enabled: showMap && filters.mapView === 'frontsite',
    staleTime: 300000,
  });

  useEffect(() => {
    if (filters.mapView === 'frontsite' && Array.isArray(frontsiteData?.orders)) {
      setFrontsiteDrawerOrders(frontsiteData.orders);
    }
  }, [filters.mapView, frontsiteData?.orders]);

  const locationTypeSummary = useMemo<LocationTypeSummary>(() => {
    const counts: LocationTypeSummary['counts'] = {
      client: locationsResponse.locations.length,
      warehouse: warehouses.length,
      vendor: vendors.length,
      prospect: prospects.length,
    };
    const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
    return { total, counts };
  }, [locationsResponse.locations.length, warehouses.length, vendors.length, prospects.length]);
  const frontsiteOrders = frontsiteData?.orders ?? [];
  const frontsiteAnalytics = useMemo(() => {
    const sourceOrders = Array.isArray(frontsiteData?.orders) && frontsiteData.orders.length > 0
      ? frontsiteData.orders
      : frontsiteDrawerOrders;
    if (!Array.isArray(sourceOrders)) {
      return {
        uniqueCompanies: 0,
        totalRecords: 0,
        completedOrders: 0,
        totalRevenue: 0,
      };
    }
    const uniqueCompanies = new Set<string>();
    let completedOrders = 0;
    let totalRevenue = 0;
    sourceOrders.forEach((order) => {
      if (order.company) {
        uniqueCompanies.add(order.company);
      }
      if ((order.payment_status || '').toLowerCase() === 'completed') {
        completedOrders += 1;
      }
      totalRevenue += Number(order.total) || 0;
    });
    return {
      uniqueCompanies: uniqueCompanies.size,
      totalRecords: sourceOrders.length,
      completedOrders,
      totalRevenue,
    };
  }, [frontsiteData?.orders, frontsiteDrawerOrders]);

  const revenueTargetCardData = useMemo(() => {
    if (filters.mapView !== 'revenue') {
      return null;
    }

    const rows = Array.isArray(locationsResponse.locations) ? locationsResponse.locations : [];
    type RepStats = { count: number; revenue: number; cities: Record<string, number> };
    type StateAccumulator = {
      target: number;
      achieved: number;
      locations: Location[];
      companies: Record<string, RevenueTargetCompanyEntry>;
      reps: Record<string, RepStats>;
      cityCounts: Record<string, number>;
    };

    const stateTotals: Record<string, StateAccumulator> = {};

    rows.forEach((location) => {
      if (!location) return;
      const locationAny = location as unknown as Record<string, unknown>;
      const rawState = typeof locationAny.state === 'string' ? locationAny.state : '';
      const stateName = rawState && rawState.trim().length > 0 ? rawState.trim() : 'Unknown';
      const targetValue = locationAny.target ?? (locationAny as { target_amount?: unknown }).target_amount ?? 0;
      const achievedValue = locationAny.total_amount ?? 0;
      const target = parseNumericAmount(targetValue);
      const achieved = parseNumericAmount(achievedValue);

      const accumulator =
        stateTotals[stateName] ?? {
          target: 0,
          achieved: 0,
          locations: [],
          companies: {},
          reps: {},
          cityCounts: {},
        };

      accumulator.target += Number.isFinite(target) ? target : 0;
      accumulator.achieved += Number.isFinite(achieved) ? achieved : 0;
      accumulator.locations.push(location);

      const rawCompany = typeof locationAny.company === 'string' ? locationAny.company.trim() : '';
      const companyName = rawCompany.length > 0 ? rawCompany : 'Unknown Company';
      const companyIdRaw = typeof locationAny.userid === 'string' ? locationAny.userid.trim() : '';
      const companyKey = companyIdRaw || `${companyName}-${Object.keys(accumulator.companies).length}`;
      const rawCity = typeof locationAny.city === 'string' ? locationAny.city.trim() : '';
      const cityName = rawCity.length > 0 ? rawCity : 'Unknown City';

      if (!accumulator.companies[companyKey]) {
        accumulator.companies[companyKey] = {
          name: companyName,
          userid: companyIdRaw || undefined,
          city: cityName !== 'Unknown City' ? cityName : undefined,
        };
      }

      const staffNameRaw = typeof locationAny.staff_name === 'string' ? locationAny.staff_name.trim() : '';
      const repName = staffNameRaw.length > 0 ? staffNameRaw : 'Unassigned';
      const repStats =
        accumulator.reps[repName] ?? {
          count: 0,
          revenue: 0,
          cities: {},
        };
      repStats.count += 1;
      repStats.revenue += Number.isFinite(achieved) ? achieved : 0;
      repStats.cities[cityName] = (repStats.cities[cityName] ?? 0) + 1;
      accumulator.reps[repName] = repStats;

      accumulator.cityCounts[cityName] = (accumulator.cityCounts[cityName] ?? 0) + 1;

      stateTotals[stateName] = accumulator;
    });

    const breakdown: RevenueTargetDrawerRow[] = [];

    Object.entries(stateTotals).forEach(([stateName, accumulator]) => {
      const safeTarget = Number.isFinite(accumulator.target) ? accumulator.target : 0;
      const safeAchieved = Number.isFinite(accumulator.achieved) ? accumulator.achieved : 0;

      let topCity: string | undefined;
      let topCityCount = 0;
      Object.entries(accumulator.cityCounts).forEach(([city, count]) => {
        if (count > topCityCount) {
          topCityCount = count;
          topCity = city !== 'Unknown City' ? city : undefined;
        }
      });

      let topSalesRepName: string | undefined;
      let topSalesRepCity: string | undefined;
      let topSalesRepCompanyCount: number | undefined;

      const repEntries = Object.entries(accumulator.reps);
      const repsData: RevenueTargetRepEntry[] = repEntries
        .map(([repName, stats]) => {
          let repTopCity: string | undefined;
          let repTopCityCount = 0;
          Object.entries(stats.cities).forEach(([city, count]) => {
            if (count > repTopCityCount) {
              repTopCityCount = count;
              repTopCity = city !== 'Unknown City' ? city : undefined;
            }
          });
          return {
            name: repName,
            companyCount: stats.count,
            revenue: stats.revenue,
            topCity: repTopCity,
            topCityCount: repTopCityCount || undefined,
            cities: stats.cities,
          };
        })
        .sort((a, b) => {
          const revenueDiff = b.revenue - a.revenue;
          if (Math.abs(revenueDiff) > 0.01) return revenueDiff;
          return b.companyCount - a.companyCount;
        });

      const topRep = repsData.find((rep) => rep.name !== 'Unassigned') ?? repsData[0];
      if (topRep) {
        topSalesRepName = topRep.name === 'Unassigned' ? undefined : topRep.name;
        topSalesRepCompanyCount = topRep.companyCount;
        topSalesRepCity = topRep.topCity;
      }

      const companies: RevenueTargetCompanyEntry[] = Object.values(accumulator.companies).sort((a, b) =>
        a.name.localeCompare(b.name),
      );

      breakdown.push({
        state: stateName,
        target: safeTarget,
        achieved: safeAchieved,
        remaining: Math.max(safeTarget - safeAchieved, 0),
        percent: safeTarget > 0 ? (safeAchieved / safeTarget) * 100 : 0,
        companyCount: companies.length,
        salesRepCount: repsData.length,
        topSalesRepName,
        topSalesRepCity,
        topSalesRepCompanyCount,
        companies,
        locations: accumulator.locations,
        reps: repsData,
        topCity,
        topCityCount,
      });
    });

    breakdown.sort((a, b) => b.target - a.target);

    const totalTarget = breakdown.reduce((sum, item) => sum + item.target, 0);
    const totalAchieved = breakdown.reduce((sum, item) => sum + item.achieved, 0);
    const totalRemaining = Math.max(totalTarget - totalAchieved, 0);
    const achievementPercent = totalTarget > 0 ? (totalAchieved / totalTarget) * 100 : 0;

    const trimmedState = typeof filters.state === 'string' ? filters.state.trim() : '';
    let highlightedState: string | undefined;
    if (trimmedState.length > 0) {
      const match = breakdown.find((item) => item.state.toLowerCase() === trimmedState.toLowerCase());
      highlightedState = match ? match.state : trimmedState;
    } else if (breakdown.length === 1) {
      highlightedState = breakdown[0]?.state;
    }

    return {
      totalTarget,
      totalAchieved,
      totalRemaining,
      achievementPercent,
      breakdown,
      highlightedState,
    };
  }, [filters.mapView, filters.state, locationsResponse.locations]);

  const revenueTargetDrawerTotalPages = useMemo(() => {
    if (!revenueTargetCardData || revenueTargetCardData.breakdown.length === 0) {
      return 0;
    }
    return Math.ceil(revenueTargetCardData.breakdown.length / REVENUE_TARGET_DRAWER_PAGE_SIZE);
  }, [revenueTargetCardData]);

  const revenueTargetDrawerSafePage =
    revenueTargetDrawerTotalPages === 0
      ? 1
      : Math.min(Math.max(revenueTargetDrawerPage, 1), revenueTargetDrawerTotalPages);

  const revenueTargetDrawerRows = useMemo(() => {
    if (!revenueTargetCardData) return [];
    const start = (revenueTargetDrawerSafePage - 1) * REVENUE_TARGET_DRAWER_PAGE_SIZE;
    return revenueTargetCardData.breakdown.slice(start, start + REVENUE_TARGET_DRAWER_PAGE_SIZE);
  }, [revenueTargetCardData, revenueTargetDrawerSafePage]);

  const revenueCompanyTotals = useMemo(() => {
    const rows = revenueCompanyDetail.rows;
    const totalTarget = rows.reduce((sum, row) => sum + row.target, 0);
    const totalAchieved = rows.reduce((sum, row) => sum + row.achieved, 0);
    return { totalTarget, totalAchieved };
  }, [revenueCompanyDetail]);

  const revenueRepTotals = useMemo(() => {
    const rows = revenueRepDetail.rows;
    const totalRevenue = rows.reduce((sum, row) => sum + row.revenue, 0);
    const totalAccounts = rows.reduce((sum, row) => sum + row.companyCount, 0);
    return { totalRevenue, totalAccounts };
  }, [revenueRepDetail]);

  useEffect(() => {
    if (revenueTargetDrawerTotalPages === 0) {
      if (revenueTargetDrawerPage !== 1) {
        setRevenueTargetDrawerPage(1);
      }
      return;
    }
    if (revenueTargetDrawerPage > revenueTargetDrawerTotalPages) {
      setRevenueTargetDrawerPage(revenueTargetDrawerTotalPages);
    }
  }, [revenueTargetDrawerPage, revenueTargetDrawerTotalPages]);

  const handleOpenRevenueTargetDrawer = useCallback(() => {
    setRevenueTargetDrawerPage(1);
    setRevenueTargetDrawerVisible(true);
  }, []);

  const handleCloseRevenueTargetDrawer = useCallback(() => {
    setRevenueTargetDrawerVisible(false);
  }, []);

  const handleCompanyLinkClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, userid?: string) => {
    if (!userid) {
      event.preventDefault();
      return;
    }
    handleEntityLinkClick(event, userid);
  }, []);

  const handleOpenRevenueCompaniesDetail = useCallback((row: RevenueTargetDrawerRow) => {
    const companyMap: Record<string, RevenueCompanyDetailRow> = {};

    row.locations.forEach((location) => {
      if (!location) return;
      const locationAny = location as unknown as Record<string, unknown>;
      const rawCompany = typeof locationAny.company === 'string' ? locationAny.company.trim() : '';
      const companyName = rawCompany.length > 0 ? rawCompany : 'Unknown Company';
      const companyIdRaw = typeof locationAny.userid === 'string' ? locationAny.userid.trim() : '';
      const companyKey = companyIdRaw || companyName;
      const rawCity = typeof locationAny.city === 'string' ? locationAny.city.trim() : '';
      const cityName = rawCity.length > 0 ? rawCity : undefined;
      const rawState = typeof locationAny.state === 'string' ? locationAny.state.trim() : undefined;
      const targetValue = locationAny.target ?? (locationAny as { target_amount?: unknown }).target_amount ?? 0;
      const achievedValue = locationAny.total_amount ?? 0;
      const salesRepRaw = typeof locationAny.staff_name === 'string' ? locationAny.staff_name.trim() : '';
      const salesRep = salesRepRaw.length > 0 ? salesRepRaw : 'Unassigned';

      const existing = companyMap[companyKey];
      const entry: RevenueCompanyDetailRow =
        existing ??
        ({
          company: companyName,
          userid: companyIdRaw || undefined,
          city: cityName,
          state: rawState,
          salesReps: [],
          target: 0,
          achieved: 0,
          locationCount: 0,
        } as RevenueCompanyDetailRow);

      entry.target += parseNumericAmount(targetValue);
      entry.achieved += parseNumericAmount(achievedValue);
      entry.locationCount += 1;
      if (!entry.salesReps.includes(salesRep)) {
        entry.salesReps.push(salesRep);
      }

      companyMap[companyKey] = entry;
    });

    const rows: RevenueCompanyDetailRow[] = Object.values(companyMap).sort(
      (a, b) => b.achieved - a.achieved,
    );

    setRevenueCompanyDetail({
      open: true,
      stateName: row.state,
      rows,
    });
  }, []);

  const handleCloseRevenueCompaniesDetail = useCallback(() => {
    setRevenueCompanyDetail((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const handleOpenRevenueRepsDetail = useCallback((row: RevenueTargetDrawerRow) => {
    const rows: RevenueRepDetailRow[] = row.reps.map((rep) => ({
      name: rep.name === 'Unassigned' ? 'Unassigned' : rep.name,
      companyCount: rep.companyCount,
      revenue: rep.revenue,
      topCity: rep.topCity,
      topCityCount: rep.topCityCount,
    }));

    setRevenueRepDetail({
      open: true,
      stateName: row.state,
      rows,
    });
  }, []);

  const handleCloseRevenueRepsDetail = useCallback(() => {
    setRevenueRepDetail((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const formatTargetPercent = (value: number): string => (Number.isFinite(value) ? `${value.toFixed(1)}%` : '0.0%');

  // Attach event handlers to the drawer content (search/pagination/edit/stats)
  useEffect(() => {
    if (filters.mapView === 'standard' || filters.mapView === 'revenue') return;
    if (!tableDrawerVisible || !tableDrawerRef.current) return;

    let searchTimer: number | null = null;

      const clickHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!tableDrawerRef.current) return;

        const columnMenu = tableDrawerRef.current.querySelector('#popup-table-column-menu') as HTMLElement | null;
        const toggleBtn = target.closest('#popup-table-column-toggle') as HTMLElement | null;
        if (toggleBtn && columnMenu) {
          const isOpen = columnMenu.getAttribute('data-open') === 'true';
          columnMenu.setAttribute('data-open', (!isOpen).toString());
          columnMenu.style.display = isOpen ? 'none' : 'block';
          e.stopPropagation();
          return;
        }

        if (columnMenu && columnMenu.getAttribute('data-open') === 'true' && !target.closest('.popup-table-column-container')) {
          columnMenu.setAttribute('data-open', 'false');
          columnMenu.style.display = 'none';
        }

      // Edit button
      const editBtn = target.closest('.edit-location-btn') as HTMLElement | null;
      if (editBtn) {
        const locationId = editBtn.getAttribute('data-location-id');
        const userId = editBtn.getAttribute('data-user-id');
        const company = editBtn.getAttribute('data-company');
        let address = editBtn.getAttribute('data-address') || '';
        const city = editBtn.getAttribute('data-city');
        const state = editBtn.getAttribute('data-state');
        const country = editBtn.getAttribute('data-country');
        let zip = editBtn.getAttribute('data-zip') || '';
        const websiteValidationNotes = editBtn.getAttribute('data-website-validation-notes');
        const websiteFoundAddress = editBtn.getAttribute('data-website-found-address');
        const websiteFoundCity = editBtn.getAttribute('data-website-found-city');
        const websiteFoundState = editBtn.getAttribute('data-website-found-state');
        const websiteFoundCountry = editBtn.getAttribute('data-website-found-country');

        if (userId) {
          const actualId = locationId || userId;

          // Use only response-provided attributes as-is
          setEditingLocationData({
            id: actualId!,
            userid: userId,
            company: company || '',
            address: address || '',
            city: city || '',
            state: state || '',
            country: country || '',
            zip: zip || '',
            website_validation_notes: websiteValidationNotes || '',
            website_found_address: websiteFoundAddress || '',
            website_found_city: websiteFoundCity || '',
            website_found_state: websiteFoundState || '',
            website_found_country: websiteFoundCountry || ''
          });
          setEditModalVisible(true);
        }
        e.stopPropagation();
        return;
      }

        // Pagination
        const pageBtn = target.closest('.popup-table-page-btn') as HTMLElement | null;
        if (pageBtn) {
          const dataEl = tableDrawerRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
          const stateEl = tableDrawerRef.current.querySelector('#popup-table-state') as HTMLTextAreaElement | null;
          if (dataEl) {
            try {
              const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
              const searchInput = tableDrawerRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
              const pageSizeSelect = tableDrawerRef.current.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
              const search = searchInput?.value || '';
              const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
              const page = parseInt(pageBtn.getAttribute('data-page') || '1');
              let sortKey: any = undefined; let sortDir: any = undefined;
              if (stateEl && stateEl.value) {
                try { const s = JSON.parse(stateEl.value || '{}'); sortKey = s.sortKey || undefined; sortDir = s.sortDir || 'asc'; } catch {}
              }
              const content = buildPopupTableContent(rows, {
                page,
                pageSize,
                search,
                sortKey,
                sortDir,
                columnVisibility: tableColumnVisibility,
                showColumnToggle: true,
              });
              setTableDrawerHTML(content);
              setTableRows(rows);
              setTableSearch(search);
            } catch (err) {
              console.error('Failed to paginate table (drawer):', err);
            }
          }
          return;
        }

        // Sorting by header click
        const sortBtn = target.closest('.popup-table-sort') as HTMLElement | null;
        if (sortBtn) {
          const dataEl = tableDrawerRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
          const stateEl = tableDrawerRef.current.querySelector('#popup-table-state') as HTMLTextAreaElement | null;
          const pageSizeSelect = tableDrawerRef.current.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
          const searchInput = tableDrawerRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
          if (dataEl) {
            try {
              const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
              const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
              const search = searchInput?.value || '';
              const key = sortBtn.getAttribute('data-sort-key') as any;
              let currentDir: 'asc' | 'desc' = 'asc';
              if (stateEl && stateEl.value) {
                try { const s = JSON.parse(stateEl.value || '{}'); if (s.sortKey === key) currentDir = s.sortDir === 'asc' ? 'desc' : 'asc'; } catch {}
              }
              const content = buildPopupTableContent(rows, {
                page: 1,
                pageSize,
                search,
                sortKey: key,
                sortDir: currentDir,
                columnVisibility: tableColumnVisibility,
                showColumnToggle: true,
              });
              setTableDrawerHTML(content);
              setTableRows(rows);
              setTableSearch(search);
            } catch (err) {
              console.error('Failed to sort table (drawer):', err);
            }
          }
          return;
        }
    };

      const inputHandler = (e: Event) => {
        const el = e.target as HTMLElement;
        if (!tableDrawerRef.current) return;
        if (el.id === 'popup-table-search') {
          if (searchTimer) window.clearTimeout(searchTimer);
          const spinner = tableDrawerRef.current!.querySelector('#popup-table-search-spinner') as HTMLElement | null;
          if (spinner) spinner.style.display = 'inline-block';
          searchTimer = window.setTimeout(() => {
            const dataEl = tableDrawerRef.current!.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
            const pageSizeSelect = tableDrawerRef.current!.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
            const stateEl = tableDrawerRef.current!.querySelector('#popup-table-state') as HTMLTextAreaElement | null;
            if (dataEl) {
              try {
                const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
                const search = (el as HTMLInputElement).value || '';
                const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
                let sortKey: any = undefined; let sortDir: any = undefined;
                if (stateEl && stateEl.value) {
                  try { const s = JSON.parse(stateEl.value || '{}'); sortKey = s.sortKey || undefined; sortDir = s.sortDir || 'asc'; } catch {}
                }
                const content = buildPopupTableContent(rows, {
                  page: 1,
                  pageSize,
                  search,
                  sortKey,
                  sortDir,
                  columnVisibility: tableColumnVisibility,
                  showColumnToggle: true,
                });
                setTableDrawerHTML(content);
                setTableRows(rows);
                setTableSearch(search);
              } catch (err) {
                console.error('Failed to filter table (drawer):', err);
              }
            }
          }, 3000);
        }
      };

    const changeHandler = (e: Event) => {
      const el = e.target as HTMLElement;
      if (!tableDrawerRef.current) return;

      if (el.classList.contains('popup-table-column-checkbox')) {
        const checkbox = el as HTMLInputElement;
        const columnKey = checkbox.getAttribute('data-column-key') as TableColumnKey | null;
        if (!columnKey) return;

        const dataEl = tableDrawerRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
        const searchInput = tableDrawerRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
        const pageSizeSelect = tableDrawerRef.current.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
        const stateEl = tableDrawerRef.current.querySelector('#popup-table-state') as HTMLTextAreaElement | null;

        if (dataEl) {
          try {
            const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
            const search = searchInput?.value || '';
            const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
            let sortKey: any = undefined;
            let sortDir: any = undefined;
            let currentPage = 1;
            if (stateEl && stateEl.value) {
              try {
                const s = JSON.parse(stateEl.value || '{}');
                sortKey = s.sortKey || undefined;
                sortDir = s.sortDir || 'asc';
                currentPage = Number.isFinite(s.page) ? Number(s.page) : 1;
              } catch {}
            }

            const nextVisibility: Record<TableColumnKey, boolean> = {
              ...tableColumnVisibility,
              [columnKey]: checkbox.checked,
            };
            nextVisibility.type = true;
            nextVisibility.name = true;

            const content = buildPopupTableContent(rows, {
              page: currentPage,
              pageSize,
              search,
              sortKey,
              sortDir,
              columnVisibility: nextVisibility,
              showColumnToggle: true,
            });

            setTableColumnVisibility(nextVisibility);
            setTableDrawerHTML(content);
            setTableRows(rows);
            setTableSearch(search);
          } catch (err) {
            console.error('Failed to update column visibility (drawer):', err);
          }
        }
        return;
      }

      if (el.id === 'popup-table-page-size') {
        const dataEl = tableDrawerRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
        const searchInput = tableDrawerRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
        const stateEl = tableDrawerRef.current.querySelector('#popup-table-state') as HTMLTextAreaElement | null;
        if (dataEl) {
          try {
            const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
            const pageSize = parseInt((el as HTMLSelectElement).value || '10');
            const search = searchInput?.value || '';
            let sortKey: any = undefined;
            let sortDir: any = undefined;
            if (stateEl && stateEl.value) {
              try {
                const s = JSON.parse(stateEl.value || '{}');
                sortKey = s.sortKey || undefined;
                sortDir = s.sortDir || 'asc';
              } catch {}
            }
            const content = buildPopupTableContent(rows, {
              page: 1,
              pageSize,
              search,
              sortKey,
              sortDir,
              columnVisibility: tableColumnVisibility,
              showColumnToggle: true,
            });
            setTableDrawerHTML(content);
            setTableRows(rows);
            setTableSearch(search);
          } catch (err) {
            console.error('Failed to change page size (drawer):', err);
          }
        }
      }
    };

    const el = tableDrawerRef.current;
    el.addEventListener('click', clickHandler);
    el.addEventListener('input', inputHandler as EventListener);
    el.addEventListener('change', changeHandler as EventListener);
    return () => {
      el.removeEventListener('click', clickHandler);
      el.removeEventListener('input', inputHandler as EventListener);
      el.removeEventListener('change', changeHandler as EventListener);
      if (searchTimer) window.clearTimeout(searchTimer);
    };
  }, [tableDrawerVisible, tableColumnVisibility, filters.mapView]);

  // Fetch primary metrics from API
  const { data: primaryMetricsData, isLoading: isLoadingPrimaryMetrics } = useQuery({
    queryKey: ['primaryMetrics', filters.country, filters.state, filters.city, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchPrimaryMetrics(filters.country, filters.state, filters.city, undefined, filters.startDate, filters.endDate),
    enabled: showMap && primaryMetricModalVisible,
    staleTime: 300000
  });

  useEffect(() => {
    // Only initialize the map when showMap is true and mapRef is available
    if (!mapRef.current || !showMap) return;

    // Clean up any existing map instance
    if (mapInstance.current) {
      mapInstance.current.setTarget(undefined);
      mapInstance.current = null;
    }

    // Create a new map instance
    const osmLayer = new TileLayer({
      source: new XYZ({
        url: 'https://tile.openstreetmap.de/{z}/{x}/{y}.png',
      }),
      preload: Infinity
    });

    const map = new Map({
      target: mapRef.current,
      layers: [osmLayer, boundaryLayer, ...Object.values(layers)],
      view: new View({
        center: fromLonLat([-119.4179, 36.7783]), // California coordinates
        zoom: 6, // Zoom level for state view
        minZoom: 1,
        maxZoom: 19
      }),
    });

    // Set up popup overlay (kept for other uses; table will open in Drawer)
    if (popupRef.current) {
      // Clean up any existing popup
      if (popup) {
        setPopup(null);
      }

      const popupOverlay = new Overlay({
        element: popupRef.current,
        positioning: 'bottom-center',
        offset: [0, -25],
        autoPan: {
          animation: {
            duration: 250
          }
        },
      });
      map.addOverlay(popupOverlay);
      setPopup(popupOverlay);

      // Add click event listener for product and order count, edit button, and table controls
      let searchTimer: number | null = null;
      const clickHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        
        // Handle edit button click
        const editBtn = target.closest('.edit-location-btn');
        if (editBtn) {
          const locationId = editBtn.getAttribute('data-location-id');
          const userId = editBtn.getAttribute('data-user-id');
          const company = editBtn.getAttribute('data-company');
          const address = editBtn.getAttribute('data-address');
          const city = editBtn.getAttribute('data-city');
          const state = editBtn.getAttribute('data-state');
          const country = editBtn.getAttribute('data-country');
          const zip = editBtn.getAttribute('data-zip');
          const websiteValidationNotes = editBtn.getAttribute('data-website-validation-notes');
          const websiteFoundAddress = editBtn.getAttribute('data-website-found-address');
          const websiteFoundCity = editBtn.getAttribute('data-website-found-city');
          const websiteFoundState = editBtn.getAttribute('data-website-found-state');
          const websiteFoundCountry = editBtn.getAttribute('data-website-found-country');
          
          if (userId) {
            // For vendors, use userid as the id if locationId is empty
            const actualId = locationId || userId;
            setEditingLocationData({
              id: actualId,
              userid: userId,
              company: company || '',
              address: address || '',
              city: city || '',
              state: state || '',
              country: country || '',
              zip: zip || '',
              website_validation_notes: websiteValidationNotes || '',
              website_found_address: websiteFoundAddress || '',
              website_found_city: websiteFoundCity || '',
              website_found_state: websiteFoundState || '',
              website_found_country: websiteFoundCountry || ''
            });
            setEditModalVisible(true);
          }
          e.stopPropagation(); // Prevent map click event
          return;
        }
        
        // Handle balance button click
        const balanceBtn = target.closest('.balance-btn');
        if (balanceBtn) {
          const action = balanceBtn.getAttribute('data-action');
          const partnerId = balanceBtn.getAttribute('data-partner-id');
          if (action === 'fetch-balance' && partnerId) {
            handleFetchSinglePartnerBalance(parseInt(partnerId), balanceBtn as HTMLElement);
          }
          e.stopPropagation(); // Prevent map click event
          return;
        }

        // Handle popup table pagination buttons
        const pageBtn = target.closest('.popup-table-page-btn') as HTMLElement | null;
        if (pageBtn && popupRef.current) {
          const dataEl = popupRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
          if (dataEl) {
            try {
              const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
              const searchInput = popupRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
              const pageSizeSelect = popupRef.current.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
              const search = searchInput?.value || '';
              const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
              const page = parseInt(pageBtn.getAttribute('data-page') || '1');
              const content = buildPopupTableContent(rows, { page, pageSize, search });
              popupRef.current.innerHTML = content;
              // keep popup position
              if (popup) popup.setPosition(popup.getPosition());
              e.stopPropagation();
              return;
            } catch (err) {
              console.error('Failed to paginate table:', err);
            }
          }
        }

      };

      // Remove any existing event listeners
      popupRef.current.removeEventListener('click', clickHandler);
      const inputHandler = (e: Event) => {
        const el = e.target as HTMLElement;
        if (!popupRef.current) return;
        // Search input live filter
        if (el.id === 'popup-table-search') {
          if (searchTimer) window.clearTimeout(searchTimer);
          // show spinner next to search while filtering
          const spinner = popupRef.current!.querySelector('#popup-table-search-spinner') as HTMLElement | null;
          if (spinner) spinner.style.display = 'inline-block';
          searchTimer = window.setTimeout(() => {
            const dataEl = popupRef.current!.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
            const pageSizeSelect = popupRef.current!.querySelector('#popup-table-page-size') as HTMLSelectElement | null;
            if (dataEl) {
              try {
                const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
                const search = (el as HTMLInputElement).value || '';
                const pageSize = pageSizeSelect ? parseInt(pageSizeSelect.value) : 10;
                const content = buildPopupTableContent(rows, { page: 1, pageSize, search });
                popupRef.current!.innerHTML = content;
                if (popup) popup.setPosition(popup.getPosition());
              } catch (err) {
                console.error('Failed to filter table:', err);
              }
            }
          }, 1000);
        }
      };
      const changeHandler = (e: Event) => {
        const el = e.target as HTMLElement;
        if (!popupRef.current) return;
        // Page size change
        if (el.id === 'popup-table-page-size') {
          const dataEl = popupRef.current.querySelector('#popup-table-data') as HTMLTextAreaElement | null;
          const searchInput = popupRef.current.querySelector('#popup-table-search') as HTMLInputElement | null;
          if (dataEl) {
            try {
              const rows: PopupTableRow[] = JSON.parse(dataEl.value || '[]');
              const pageSize = parseInt((el as HTMLSelectElement).value || '10');
              const search = searchInput?.value || '';
              const content = buildPopupTableContent(rows, { page: 1, pageSize, search });
              popupRef.current.innerHTML = content;
              if (popup) popup.setPosition(popup.getPosition());
            } catch (err) {
              console.error('Failed to change page size:', err);
            }
          }
        }
      };
      // Add new event listener
      popupRef.current.removeEventListener('input', inputHandler as EventListener);
      popupRef.current.removeEventListener('change', changeHandler as EventListener);
      popupRef.current.addEventListener('click', clickHandler);
      popupRef.current.addEventListener('input', inputHandler as EventListener);
      popupRef.current.addEventListener('change', changeHandler as EventListener);
    }

    mapInstance.current = map;

    // Clean up function
    return () => {
      if (mapInstance.current) {
        mapInstance.current.setTarget(undefined);
        mapInstance.current = null;
      }
      if (popup) {
        setPopup(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap, layers, styles]);

  useEffect(() => {
    if (!mapInstance.current || !showMap) return;

    // Hide popup when filters change
    if (popup) {
      popup.setPosition(undefined);
    }

    // Clear existing features
    Object.entries(sources).forEach(([key, source]) => {
      if (filters.mapView === 'sales-pipeline' && key === 'client') {
        return;
      }
      const vectorSource = source.getSource() as VectorSource;
      vectorSource.clear();
    });
    Object.values(supplyHubSources).forEach((source) => {
      source.clear();
    });

    // Create an array to store the bounds of all features
    const bounds: number[] = [];

    // Add features in batches for better performance
    const addFeatures = (items: (Location | Warehouse | Vendor | Prospect)[], type: LocationType, clusterSource: Cluster) => {
      if (filters.mapView !== 'revenue') {
        if (!filters.locationType.includes('all') && !filters.locationType.includes(type)) return;
      }
      
      // Filter items based on search criteria
      const filteredItems = items
        .filter(item => {
          if (!item.coordinates) return false;
          
          // Apply search term filter
          if (filters.searchTerm) {
            const company = (item as any).company || (item as any).name || '';
            if (!company.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
              return false;
            }
          }

          // Apply product search filter for applicable types
          if (filters.productSearch && (type === 'client' || type === 'vendor')) {
            // For clients and vendors, the API response is already filtered by product
            return true;
          }

          return true;
        });

      const features = filteredItems
        .map(item => {
          const coords = fromLonLat(item.coordinates);
          bounds.push(...coords);
          
          // Map data based on type
          let properties;
          switch (type) {
            case 'prospect':
              const prospect = item as Prospect;
              properties = {
                id: (prospect as any).id || '',
                userid: prospect.userid,
                company: prospect.company || "",
                address: (prospect as any).address || "",
                city: prospect.city || "",
                state: prospect.state || "",
                zip: (prospect as any).zip || "",
                country: prospect.country ? { short_name: prospect.country.short_name } : null,
                coordinates: prospect.coordinates,
                staff_name: prospect.staff_name || "",
                quotation_count: prospect.quotation_count ?? 0,
                enquiry_count: prospect.enquiry_count ?? 0,
                target: prospect.target ?? 0,
                target_year: prospect.target_year ?? null,
                type
              };
              break;
            case 'warehouse':
              const warehouse = item as Warehouse;
              properties = {
                name: warehouse.name || "",
                city: warehouse.city || "",
                state: warehouse.state || "",
                country: warehouse.country || null,
                coordinates: warehouse.coordinates,
                type,
                address: warehouse.address || "",
                zip: warehouse.zip || ""
              };
              break;
            case 'vendor':
              const vendor = item as Vendor;
              properties = {
                id: vendor.id || vendor.userid,  // Add id field for vendors
                userid: vendor.userid,  // Add userid field
                company: vendor.company || "",
                city: vendor.city || "",
                state: vendor.state || "",
                country: vendor.country || null,
                coordinates: vendor.coordinates,
                type,
                address: vendor.address || "",
                zip: vendor.zip || "",
                email_1: vendor.email_1 || "",
                phonenumber: vendor.phonenumber || ""
              };
              break;
            default:
              // For client type
              const location = item as Location;
              properties = {
                ...location,
                type
              };
          }
          
          return new Feature({
            geometry: new Point(coords),
            properties
          });
        });

      const vectorSource = clusterSource.getSource() as VectorSource;
      vectorSource.addFeatures(features);
      if (typeof clusterSource.refresh === 'function') {
        clusterSource.refresh();
      }
    };

    const addProductFeatures = (items: ProductCompanyLocation[], clusterSource: Cluster) => {
      const vectorSource = clusterSource.getSource() as VectorSource;

      const features = items
        .filter((location) => Number.isFinite(location.latitude) && Number.isFinite(location.longitude))
        .map((location) => {
          const coords = fromLonLat([location.longitude, location.latitude]);
          bounds.push(...coords);

          const productTotals = location.products.reduce(
            (acc, product) => {
              const quantity = Number(product.quantity) || 0;
              const rate = Number(product.rate) || 0;
              return {
                quantity: acc.quantity + quantity,
                amount: acc.amount + quantity * rate,
              };
            },
            { quantity: 0, amount: 0 },
          );

          const feature = new Feature({
            geometry: new Point(coords),
          });

          const uniqueProductCount = computeLocationUniqueProductCount(location);
          const bucketSource = resolvedProductBuckets.length > 0 ? resolvedProductBuckets : DEFAULT_PRODUCT_BUCKETS;
          const featureBucket = selectProductMarkerBucket(bucketSource, uniqueProductCount);

          feature.set('properties', {
            type: 'product',
            company: location.company,
            userid: location.userid ? String(location.userid) : undefined,
            city: location.city,
            state: location.state,
            country: location.country,
            total_quantity: productTotals.quantity,
            total_amount: productTotals.amount,
            products: location.products,
            productLocation: location,
            uniqueProductCount,
            uniqueProductBucketKey: featureBucket.key,
          });

          feature.set('productLocation', location);
          feature.set('totalQuantity', productTotals.quantity);
          feature.set('totalAmount', productTotals.amount);
          feature.set('uniqueProductCount', uniqueProductCount);
          feature.set('uniqueProductBucketKey', featureBucket.key);
          feature.set('uniqueProductBucketColor', featureBucket.color);

          return feature;
        });

      vectorSource.addFeatures(features);
      if (typeof clusterSource.refresh === 'function') {
        clusterSource.refresh();
      }
    };

    const addFrontsiteFeatures = (orders: FrontsiteOrder[], clusterSource: Cluster) => {
      const vectorSource = clusterSource.getSource() as VectorSource;
      const features = orders
        .filter((order) => Array.isArray(order.coordinates) && order.coordinates?.length === 2)
        .map((order) => {
          const coords = fromLonLat(order.coordinates as [number, number]);
          bounds.push(...coords);
          const totalAmount = Number.isFinite(order.total) && order.total > 0
            ? order.total
            : Number(order.client_total_amount) || 0;
          const feature = new Feature({
            geometry: new Point(coords),
            properties: {
              type: 'frontsite',
              total_amount: totalAmount,
              company: order.company || '',
              city: order.city || '',
              state: order.state || '',
              country: order.country?.short_name || (typeof order.country === 'string' ? order.country : ''),
            },
          });
          feature.set('frontsiteOrder', order);
          feature.set('properties', {
            type: 'frontsite',
            total_amount: totalAmount,
            company: order.company || '',
            city: order.city || '',
            state: order.state || '',
            country: order.country?.short_name || (typeof order.country === 'string' ? order.country : ''),
          });
          return feature;
        });
      vectorSource.addFeatures(features);
      if (typeof clusterSource.refresh === 'function') {
        clusterSource.refresh();
      }
    };

    const addSupplyHubFeatures = () => {
      supplyHubDisplayWarehouses.forEach(({ warehouse, totalOrders }) => {
        if (!isFiniteNumber(warehouse.warehouse_lat) || !isFiniteNumber(warehouse.warehouse_long)) {
          return;
        }

        const coordinate = fromLonLat([warehouse.warehouse_long, warehouse.warehouse_lat]);
        bounds.push(...coordinate);

        const feature = new Feature({
          geometry: new Point(coordinate),
          properties: {
            type: 'supply-hub-warehouse',
            warehouse,
            totalOrders,
          },
        });

        feature.set('supplyHubType', 'warehouse');
        feature.set('warehouse', warehouse);
        feature.set('totalOrders', totalOrders);
        feature.setStyle(getSupplyHubWarehouseStyles(warehouse, totalOrders));

        supplyHubSources.warehouses.addFeature(feature);
      });

      supplyHubDisplayVendorGroups.forEach((group) => {
        const location = group.location;
        if (!isFiniteNumber(location.latitude) || !isFiniteNumber(location.longitude)) {
          return;
        }

        const vendorCoordinate = fromLonLat([location.longitude, location.latitude]);
        bounds.push(...vendorCoordinate);

        const vendorFeature = new Feature({
          geometry: new Point(vendorCoordinate),
          properties: {
            type: 'supply-hub-vendor',
            vendorGroup: group,
          },
        });

        vendorFeature.set('supplyHubType', 'vendor');
        vendorFeature.set('vendorGroup', group);
        vendorFeature.setStyle(getSupplyHubVendorStyles(group));

        supplyHubSources.vendors.addFeature(vendorFeature);

        const warehouse = group.warehouse;
        if (!isFiniteNumber(warehouse.warehouse_lat) || !isFiniteNumber(warehouse.warehouse_long)) {
          return;
        }
        const warehouseCoordinate = fromLonLat([warehouse.warehouse_long, warehouse.warehouse_lat]);

        const shippingEntries =
          supplyHubModeFilter === 'all'
            ? (Object.entries(group.shippingSummary) as Array<[SupplyHubShippingMode, { count: number; orders: SupplyHubPurchaseOrder[]; totalValue: number }]>)
            : ([
                [
                  supplyHubModeFilter,
                  group.shippingSummary[supplyHubModeFilter],
                ],
              ] as Array<[SupplyHubShippingMode, { count: number; orders: SupplyHubPurchaseOrder[]; totalValue: number }]>);

        shippingEntries.forEach(([mode, summary]) => {
          if (!summary || summary.count === 0) {
            return;
          }

          const curvedCoordinates = buildCurvedRouteCoordinates(warehouseCoordinate, vendorCoordinate);
          curvedCoordinates.forEach((coordinate) => bounds.push(...coordinate));

          const route = new Feature({
            geometry: new LineString(curvedCoordinates),
            properties: {
              type: 'supply-hub-route',
              shippingMode: mode,
              orders: summary.orders,
              vendorGroup: group,
            },
          });

          route.set('supplyHubType', 'route');
          route.set('shippingMode', mode);
          route.set('orders', summary.orders);
          route.set('vendorGroup', group);
          route.set('orderCount', summary.count);
          route.setStyle(getSupplyHubRouteStyles(mode, summary.count));

          supplyHubSources.routes.addFeature(route);
        });
      });

      supplyHubSources.routes.changed();
      supplyHubSources.vendors.changed();
      supplyHubSources.warehouses.changed();
    };

    // Add features in a single animation frame for better performance
    requestAnimationFrame(() => {
      if (filters.mapView === 'product') {
        addProductFeatures(productLocations, sources.product);
      } else if (filters.mapView === 'supply-hub') {
        addSupplyHubFeatures();
      } else if (filters.mapView === 'frontsite') {
        addFrontsiteFeatures(frontsiteOrders, sources.client);
      } else if (filters.mapView !== 'sales-pipeline') {
        addFeatures(locationsResponse.locations, 'client', sources.client);
        if (filters.mapView !== 'revenue') {
          addFeatures(warehouses, 'warehouse', sources.warehouse);
          addFeatures(vendors, 'vendor', sources.vendor);
          if (Array.isArray(prospects)) {
            addFeatures(prospects, 'prospect', sources.prospect);
          }
        }
      }

      // Update layer visibility
      Object.entries(layers).forEach(([type, layer]) => {
        if (type === 'product') {
          const shouldShowProductLayer = filters.mapView === 'product';
          layer.setVisible(shouldShowProductLayer);
          if (shouldShowProductLayer) {
            layer.setStyle(productClusterStyle);
          }
          return;
        }

        if (type === 'supplyHubRoutes' || type === 'supplyHubVendors' || type === 'supplyHubWarehouses') {
          const shouldShowSupplyHubLayer = filters.mapView === 'supply-hub';
          layer.setVisible(shouldShowSupplyHubLayer);
          if (!shouldShowSupplyHubLayer) {
            return;
          }
          layer.setStyle(undefined as any);
          return;
        }

        const locationType = type as LocationType;
        // In pipeline and revenue views, only show the client layer
        let shouldShow = true;
        if (filters.mapView === 'sales-pipeline') {
          shouldShow = locationType === 'client';
        } else if (filters.mapView === 'revenue' || filters.mapView === 'frontsite') {
          shouldShow = locationType === 'client';
        } else if (filters.mapView === 'product') {
          shouldShow = false;
        } else {
          // Hide prospect layer during product search
          shouldShow = (locationType === 'prospect' && filters.productSearch)
            ? false
            : filters.locationType.includes('all') || filters.locationType.includes(locationType);
        }
        layer.setVisible(shouldShow);
        // Apply style depending on map view
        if (locationType === 'client') {
          if (filters.mapView === 'sales-pipeline') {
            layer.setStyle(pipelineClusterStyle as any);
          } else if (filters.mapView === 'revenue') {
            layer.setStyle(createRevenueStyle());
          } else if (filters.mapView === 'frontsite') {
            layer.setStyle(createFrontsiteStyle());
          } else {
            layer.setStyle(createClusterStyle('client'));
          }
        } else {
          layer.setStyle(createClusterStyle(locationType));
        }
      });

      if (filters.mapView !== 'sales-pipeline') {
        const boundaryExtent = boundarySource.getExtent?.();
        const hasBoundaryExtent =
          boundaryExtent &&
          Array.isArray(boundaryExtent) &&
          boundaryExtent.length === 4 &&
          boundaryExtent.every((value) => Number.isFinite(value));

        const shouldUseBoundaryExtent = filters.mapView === 'standard' && hasBoundaryExtent;

        const shouldAutoFit =
          filters.mapView === 'revenue'
            ? !revenueAutoFitRef.current
            : filters.mapView === 'product'
              ? !productAutoFitRef.current
              : filters.mapView === 'frontsite'
                ? !frontsiteAutoFitRef.current
                : true;

        if (!shouldAutoFit) {
          return;
        }

        if (shouldUseBoundaryExtent) {
          mapInstance.current?.getView().fit(boundaryExtent as number[], {
            duration: 1000,
            padding: [50, 50, 50, 50],
          });
        } else if (bounds.length > 0) {
          const minX = Math.min(...bounds.filter((_, i) => i % 2 === 0));
          const minY = Math.min(...bounds.filter((_, i) => i % 2 === 1));
          const maxX = Math.max(...bounds.filter((_, i) => i % 2 === 0));
          const maxY = Math.max(...bounds.filter((_, i) => i % 2 === 1));

          const padding = 50;
          const extent = [minX - padding, minY - padding, maxX + padding, maxY + padding];

          mapInstance.current?.getView().fit(extent, {
            duration: 1000,
            padding: [50, 50, 50, 50],
          });
        } else if (hasBoundaryExtent) {
          mapInstance.current?.getView().fit(boundaryExtent as number[], {
            duration: 1000,
            padding: [50, 50, 50, 50],
          });
        } else if (!mapInstance.current?.getView().getCenter()) {
          mapInstance.current?.getView().setCenter(fromLonLat([-119.4179, 36.7783]));
          mapInstance.current?.getView().setZoom(6);
        }

        if (filters.mapView === 'revenue') {
          revenueAutoFitRef.current = true;
        } else if (filters.mapView === 'product') {
          productAutoFitRef.current = true;
        } else if (filters.mapView === 'frontsite') {
          frontsiteAutoFitRef.current = true;
        }
      }
    });

    const pipelineData = salesPipelineResponse?.pipeline_data ?? [];
    const totalPipelineEntries = pipelineData.length;
    const totalPipelineEntriesForFilter =
      pipelineStageFilter === 'ALL'
        ? totalPipelineEntries
        : pipelineData.filter(
            (entry) => ((entry?.current_stage ?? 'ENQUIRY') as SalesPipelineStage) === pipelineStageFilter,
          ).length;

    // Handle clicks with cluster support
    const clickHandler = (evt: any) => {
      const feature = mapInstance.current?.forEachFeatureAtPixel(evt.pixel, (feature: any) => feature);

      if (!feature) {
        if (popup) popup.setPosition(undefined);
        if (filters.mapView === 'supply-hub') {
          setSupplyHubDrawerContext(null);
          setSupplyHubDrawerVisible(false);
        }
        return;
      }

      if (filters.mapView === 'supply-hub') {
        if (popup) {
          popup.setPosition(undefined);
        }

        setTableDrawerVisible(false);
        setProductDrawerVisible(false);

        const unwrapFeature = (feat: any) => {
          const nested = feat?.get?.('features');
          if (Array.isArray(nested) && nested.length > 0) {
            return nested[0];
          }
          return feat;
        };

        const targetFeature = unwrapFeature(feature);
        const supplyHubType = targetFeature?.get?.('supplyHubType');

        if (!supplyHubType) {
          return;
        }

        if (supplyHubType === 'vendor') {
          const vendorGroup = targetFeature.get('vendorGroup') as SupplyHubVendorGroup | undefined;
          if (vendorGroup) {
            setSupplyHubDrawerContext({ type: 'vendor', vendorGroup });
            setSupplyHubDrawerVisible(true);
          }
        } else if (supplyHubType === 'route') {
          const vendorGroup = targetFeature.get('vendorGroup') as SupplyHubVendorGroup | undefined;
          const shippingMode = targetFeature.get('shippingMode') as SupplyHubShippingMode | undefined;
          const orders = targetFeature.get('orders') as SupplyHubPurchaseOrder[] | undefined;
          if (vendorGroup && shippingMode) {
            setSupplyHubDrawerContext({
              type: 'route',
              vendorGroup,
              shippingMode,
              orders: Array.isArray(orders) ? orders : [],
            });
            setSupplyHubDrawerVisible(true);
          }
        } else if (supplyHubType === 'warehouse') {
          const warehouse = targetFeature.get('warehouse') as SupplyHubWarehouse | undefined;
          if (warehouse) {
            const key = buildSupplyHubWarehouseKey(warehouse);
            const vendorGroups = supplyHubDisplayVendorGroupsByWarehouse.get(key) ?? [];
            const totalForWarehouse = vendorGroups.reduce((acc, group) => acc + group.purchaseOrders.length, 0);
            setSupplyHubDrawerContext({
              type: 'warehouse',
              warehouse,
              vendorGroups,
              totalPurchaseOrders: totalForWarehouse,
            });
            setSupplyHubDrawerVisible(true);
          }
        }
        return;
      }

      if (!popup) {
        return;
      }

      // Only react to our location markers/clusters. Ignore boundary polygons or other features.
      const tryGetProps = (f: any) => (f && typeof f.get === 'function') ? f.get('properties') : undefined;
      const isLocationType = (t: any) =>
        t === 'client' ||
        t === 'warehouse' ||
        t === 'vendor' ||
        t === 'prospect' ||
        t === 'product' ||
        t === 'frontsite';

      const clustered = feature.get && feature.get('features');
      if (Array.isArray(clustered) && clustered.length > 0) {
        const hasAnyLocation = clustered.some((f: any) => {
          const p = tryGetProps(f);
          return p && isLocationType(p.type);
        });
        if (!hasAnyLocation) {
          // Clicked a cluster that doesn't contain our location features
          return;
        }
      } else {
        const props = tryGetProps(feature);
        if (!props || !isLocationType(props.type)) {
          // Not a location marker; ignore (e.g., boundary polygon)
          return;
        }
      }

      if (filters.mapView === 'product') {
        if (popup) {
          popup.setPosition(undefined);
        }

        const collectedLocations = new globalThis.Map<string, ProductCompanyLocation>();

        const expandedFeatures: FeatureLike[] = [];
        if (Array.isArray(clustered) && clustered.length > 0) {
          clustered.forEach((inner: FeatureLike) => expandedFeatures.push(inner));
        } else {
          expandedFeatures.push(feature as FeatureLike);
        }

        expandedFeatures.forEach((expandedFeature) => {
          const inner = (expandedFeature as any).get?.('features');
          if (Array.isArray(inner) && inner.length > 0) {
            inner.forEach((nested: FeatureLike) => collectProductLocationIntoMap(nested, collectedLocations));
          } else {
            collectProductLocationIntoMap(expandedFeature, collectedLocations);
          }
        });

        const locationsList = Array.from(collectedLocations.values());
        const bucketSource = resolvedProductBuckets.length > 0 ? resolvedProductBuckets : DEFAULT_PRODUCT_BUCKETS;
        const filteredLocations = activeProductBucketKey
          ? locationsList.filter((loc) => {
              const locBucket = selectProductMarkerBucket(
                bucketSource,
                computeLocationUniqueProductCount(loc),
              );
              return locBucket.key === activeProductBucketKey;
            })
          : locationsList;

        setProductDrawerLocations(filteredLocations);
        if (filteredLocations.length > 0) {
          setProductDrawerTitle(computeProductDrawerTitle(filteredLocations));
          setProductDrawerVisible(true);
        } else {
          setProductDrawerTitle('Product Insights');
          setProductDrawerVisible(false);
        }

        setTableDrawerVisible(false);
        setTableDrawerHTML('');
        setTableRows([]);
        setTableSearch('');
        setPipelineDrawerEntries([]);
        setPipelineDrawerContext('overall');
        setPipelineDrawerMetricsOverride(null);
        return;
      }

      const features = feature.get('features');
      
      // If it's a cluster with multiple features
      if (features && features.length > 1) {
        if (filters.mapView === 'frontsite') {
          const orders = features
            .map((f: Feature<Point>) => f.get('frontsiteOrder') as FrontsiteOrder | undefined)
            .filter(
              (order: FrontsiteOrder | undefined): order is FrontsiteOrder =>
                Boolean(order),
            );
          openFrontsiteDrawerOrders(orders);
          return;
        }
        
        if (filters.mapView === 'sales-pipeline') {
          const pipelineEntries = features
            .map((f: Feature<Point>) => extractPipelineEntry(f))
            .filter((entry: SalesPipelineEntry | undefined): entry is SalesPipelineEntry => Boolean(entry));
          if (pipelineEntries.length > 0) {
            setPipelineDrawerEntries(pipelineEntries);
            const representsOverall =
              totalPipelineEntriesForFilter > 0 && pipelineEntries.length >= totalPipelineEntriesForFilter;
            setPipelineDrawerContext(representsOverall ? 'overall' : 'subset');
            setPipelineDrawerMetricsOverride(computePipelineDrawerMetricsOverride(pipelineEntries));
            setTableDrawerVisible(true);
            setTableDrawerHTML('');
            setTableRows([]);
            setTableSearch('');
          } else {
            setPipelineDrawerEntries([]);
            setPipelineDrawerContext('overall');
            setPipelineDrawerMetricsOverride(null);
            setTableDrawerVisible(false);
          }
          return;
        }

        const currentZoom = mapInstance.current?.getView().getZoom() || 0;

        // Check if features share same coordinates
        const firstGeometry = features[0].getGeometry();
        if (!firstGeometry) return;

        const firstCoords = firstGeometry.getCoordinates();
        const allSameLocation = features.every((f: Feature<Point>) => {
          const geometry = f.getGeometry();
          if (!geometry) return false;

          const coords = geometry.getCoordinates();
          return coords[0] === firstCoords[0] && coords[1] === firstCoords[1];
        });

        // If at max zoom or all features are at same location, show listing in right drawer (as a table)
        if (currentZoom >= 16 || allSameLocation) {
          try {
            const rows: PopupTableRow[] = features.map((f: Feature<Point>) => mapFeatureToPopupRow(f));
            if (filters.mapView === 'standard' || filters.mapView === 'revenue') {
              openReactTableDrawer(rows);
              return;
            }
            const content = buildPopupTableContent(rows, {
              page: 1,
              pageSize: 10,
              search: '',
              columnVisibility: tableColumnVisibility,
              showColumnToggle: true,
            });
            setTableDrawerHTML(content);
            setTableDrawerVisible(true);
            setTableRows(rows);
            setTableSearch('');
          } catch (error) {
            console.error('Error creating drawer content:', error);
          }
          return;
        }

        // Show cluster listing in right drawer (as a table)
        try {
          const rows: PopupTableRow[] = features.map((f: Feature<Point>) => mapFeatureToPopupRow(f));
          if (filters.mapView === 'standard' || filters.mapView === 'revenue') {
            openReactTableDrawer(rows);
            return;
          }
          const content = buildPopupTableContent(rows, {
            page: 1,
            pageSize: 10,
            search: '',
            columnVisibility: tableColumnVisibility,
            showColumnToggle: true,
          });
          setTableDrawerHTML(content);
          setTableDrawerVisible(true);
          setTableRows(rows);
          setTableSearch('');
        } catch (error) {
          console.error('Error creating drawer content:', error);
        }
        return;
      }

      // Single feature - show right drawer with one-row table
      const actualFeature = features ? features[0] : feature;

      if (filters.mapView === 'sales-pipeline') {
        const entry = extractPipelineEntry(actualFeature as FeatureLike);
        if (entry) {
          setPipelineDrawerEntries([entry]);
          setPipelineDrawerContext('subset');
          setPipelineDrawerMetricsOverride(computePipelineDrawerMetricsOverride([entry]));
          setTableDrawerVisible(true);
          setTableDrawerHTML('');
          setTableRows([]);
          setTableSearch('');
        } else {
          setPipelineDrawerEntries([]);
          setPipelineDrawerContext('overall');
          setPipelineDrawerMetricsOverride(null);
          setTableDrawerVisible(false);
        }
        return;
      }

      if (filters.mapView === 'frontsite') {
        const order = (actualFeature as any)?.get?.('frontsiteOrder') as FrontsiteOrder | undefined;
        openFrontsiteDrawerOrders(order ? [order] : []);
        return;
      }

      try {
        const row: PopupTableRow = mapFeatureToPopupRow(actualFeature as FeatureLike);
        if (filters.mapView === 'standard' || filters.mapView === 'revenue') {
          openReactTableDrawer([row]);
          setPipelineDrawerMetricsOverride(null);
          return;
        }
        const content = buildPopupTableContent([row], {
          page: 1,
          pageSize: 10,
          search: '',
          columnVisibility: tableColumnVisibility,
          showColumnToggle: true,
        });
        setTableDrawerHTML(content);
        setTableDrawerVisible(true);
        setTableRows([row]);
        setTableSearch('');
        setPipelineDrawerMetricsOverride(null);
      } catch (error) {
        console.error('Error creating drawer content:', error);
      }
    };

    const map = mapInstance.current;
    map.un('click', clickHandler);
    map.on('click', clickHandler);

    return () => {
      map.un('click', clickHandler);
    };
  }, [
    locationsResponse,
    warehouses,
    vendors,
    prospects,
    frontsiteOrders,
    filters,
    popup,
    sources,
    layers,
    showMap,
    productLocations,
    productClusterStyle,
    pipelineClusterStyle,
    salesPipelineResponse,
    pipelineStageFilter,
    computePipelineDrawerMetricsOverride,
    resolvedProductBuckets,
    activeProductBucketKey,
    supplyHubDisplayVendorGroups,
    supplyHubDisplayWarehouses,
    supplyHubDisplayVendorGroupsByWarehouse,
    supplyHubModeFilter,
    boundarySource,
    openReactTableDrawer,
    openFrontsiteDrawerOrders,
  ]);

  useEffect(() => {
    const clientCluster = sources.client;
    const clientVector = clientCluster.getSource() as VectorSource;
    clientVector.clear();

    if (filters.mapView !== 'sales-pipeline') {
      clientVector.changed();
      clientCluster.changed();
      if (typeof clientCluster.refresh === 'function') {
        clientCluster.refresh();
      }
      return;
    }

    pipelineStyleCache.current.single.clear();
    pipelineStyleCache.current.cluster.clear();

    const pipelineBounds: number[] = [];
    const normalizedCompanySearch = filters.searchTerm?.trim().toLowerCase();

    (salesPipelineResponse?.pipeline_data || []).forEach((entry) => {
      const coords = entry.coordinates;
      if (!Array.isArray(coords) || coords.length !== 2) {
        return;
      }
      const [lon, lat] = coords;
      if (typeof lon !== 'number' || typeof lat !== 'number') {
        return;
      }

      const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
      if (pipelineStageFilter !== 'ALL' && stage !== pipelineStageFilter) {
        return;
      }

      if (normalizedCompanySearch) {
        const companyName = (entry.company || '').toLowerCase();
        if (!companyName.includes(normalizedCompanySearch)) {
          return;
        }
      }

      const projected = fromLonLat([lon, lat]);
      if (!Number.isFinite(projected[0]) || !Number.isFinite(projected[1])) {
        return;
      }
      pipelineBounds.push(projected[0], projected[1]);

      const feature = new Feature({
        geometry: new Point(projected),
        pipelineEntry: entry,
      });

      feature.set('pipelineStage', stage);
      feature.set('properties', {
        type: 'client',
        userid: entry.clientid ? String(entry.clientid) : '',
        company: entry.company || '',
        city: entry.city || '',
        state: entry.state || '',
        zip: entry.zipcode || '',
        address: entry.address || '',
        country: entry.country ? entry.country.short_name : '',
        staff_name: entry.staff_name || '',
        total_amount:
          entry.stage_values?.net_invoice_value ??
          entry.stage_values?.sales_order_value ??
          entry.stage_values?.quotation_value ??
          0,
        pipelineStage: stage,
        pipelineEntry: entry,
        pipelineCounts: entry.stage_counts,
        pipelineDates: entry.stage_dates,
        pipelineValues: entry.stage_values,
      });

      clientVector.addFeature(feature);
    });

    if (pipelineBounds.length > 0) {
      const xs = pipelineBounds.filter((_, index) => index % 2 === 0);
      const ys = pipelineBounds.filter((_, index) => index % 2 === 1);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const padding = 50;
      mapInstance.current?.getView().fit([minX - padding, minY - padding, maxX + padding, maxY + padding], {
        duration: 800,
        padding: [50, 50, 50, 50],
      });
    } else if (!mapInstance.current?.getView().getCenter()) {
      mapInstance.current?.getView().setCenter(fromLonLat([-119.4179, 36.7783]));
      mapInstance.current?.getView().setZoom(6);
    }

    clientVector.changed();
    clientCluster.changed();
    if (typeof clientCluster.refresh === 'function') {
      clientCluster.refresh();
    }
  }, [filters.mapView, salesPipelineResponse, sources.client, pipelineStageFilter, filters.searchTerm]);


  const isLoading =
    filters.mapView === 'sales-pipeline'
      ? isLoadingSalesPipeline
      : filters.mapView === 'product'
        ? isLoadingProductLocations
        : filters.mapView === 'supply-hub'
          ? isLoadingSupplyHub
          : filters.mapView === 'frontsite'
            ? isLoadingFrontsite
            : (isLoadingLocations || isLoadingWarehouses || isLoadingVendors || isLoadingProspects);

  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    const rafId = requestAnimationFrame(() => map.updateSize());
    const timeoutId = window.setTimeout(() => map.updateSize(), 120);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [tableDrawerVisible, drawerVisible]);

  const handleShowListClick = useCallback(() => {
    setDrawerVisible(false);

    if (filters.mapView === 'sales-pipeline') {
      const normalizedCompanySearch = filters.searchTerm?.trim().toLowerCase();
      const entries = (salesPipelineResponse?.pipeline_data || []).filter((entry) => {
        if (!entry) {
          return false;
        }
        const coords = entry.coordinates;
        if (!Array.isArray(coords) || coords.length !== 2) {
          return false;
        }
        const [lon, lat] = coords;
        if (typeof lon !== 'number' || typeof lat !== 'number') {
          return false;
        }
        const stage = (entry.current_stage ?? 'ENQUIRY') as SalesPipelineStage;
        if (pipelineStageFilter !== 'ALL' && stage !== pipelineStageFilter) {
          return false;
        }
        if (normalizedCompanySearch) {
          const companyName = (entry.company || '').toLowerCase();
          if (!companyName.includes(normalizedCompanySearch)) {
            return false;
          }
        }
        return true;
      });

      setPipelineDrawerEntries(entries);
      const pipelineData = salesPipelineResponse?.pipeline_data || [];
      const totalForFilter =
        pipelineStageFilter === 'ALL'
          ? pipelineData.length
          : pipelineData.filter(
              (entry) => ((entry?.current_stage ?? 'ENQUIRY') as SalesPipelineStage) === pipelineStageFilter,
            ).length;
      const representsOverall = totalForFilter > 0 && entries.length >= totalForFilter;
      setPipelineDrawerContext(representsOverall ? 'overall' : 'subset');
      setPipelineDrawerMetricsOverride(computePipelineDrawerMetricsOverride(entries));
      setTableDrawerHTML('');
      setTableRows([]);
      setTableSearch('');
      setTableDrawerVisible(true);
      return;
    }

    if (filters.mapView === 'frontsite') {
      openFrontsiteDrawerOrders(frontsiteOrders);
      setPipelineDrawerEntries([]);
      setTableDrawerHTML('');
      setTableRows([]);
      setTableSearch('');
      return;
    }

    if (filters.mapView === 'product') {
      const productCluster = sources.product;
      const vectorSource = productCluster?.getSource?.();
      const collectedLocations = new globalThis.Map<string, ProductCompanyLocation>();

      if (vectorSource && typeof vectorSource.getFeatures === 'function') {
        vectorSource.getFeatures().forEach((feature: FeatureLike) => {
          collectProductLocationIntoMap(feature, collectedLocations);
        });
      } else if (Array.isArray(productLocations) && productLocations.length > 0) {
        productLocations.forEach((location) => {
          const key = buildProductLocationKey(location);
          if (!collectedLocations.has(key)) {
            collectedLocations.set(key, location);
          }
        });
      }

      const locationsList = Array.from(collectedLocations.values());
      const bucketSource = resolvedProductBuckets.length > 0 ? resolvedProductBuckets : DEFAULT_PRODUCT_BUCKETS;
      const filteredLocations = activeProductBucketKey
        ? locationsList.filter((loc) => {
            const locBucket = selectProductMarkerBucket(
              bucketSource,
              computeLocationUniqueProductCount(loc),
            );
            return locBucket.key === activeProductBucketKey;
          })
        : locationsList;

      setProductDrawerLocations(filteredLocations);
      if (filteredLocations.length > 0) {
        setProductDrawerTitle(computeProductDrawerTitle(filteredLocations));
      } else {
        setProductDrawerTitle('Product Insights');
      }
      setProductDrawerVisible(true);

      setTableDrawerVisible(false);
      setTableDrawerHTML('');
      setTableRows([]);
      setTableSearch('');
      setPipelineDrawerEntries([]);
      setPipelineDrawerContext('overall');
      setPipelineDrawerMetricsOverride(null);
      return;
    }

    if (filters.mapView === 'revenue') {
      const clientRows = tableRowsByType.client || [];
      openReactTableDrawer(clientRows, true);
      setPipelineDrawerEntries([]);
      setPipelineDrawerMetricsOverride(null);
      setTableDrawerHTML('');
      return;
    }

    const locationSelections = Array.isArray(filters.locationType) ? filters.locationType : [];
    const hasAllSelected = locationSelections.includes('all' as LocationType);
    const clusterKeys: Array<keyof typeof sources> = ['client', 'warehouse', 'vendor', 'prospect'];

    const shouldIncludeType = (key: keyof typeof sources) => {
      if (filters.mapView === 'revenue') {
        return key === 'client';
      }
      if (key === 'prospect' && filters.productSearch) {
        return false;
      }
      return hasAllSelected || locationSelections.includes(key as LocationType);
    };

    const rows: PopupTableRow[] = [];

    clusterKeys.forEach((key) => {
      if (!shouldIncludeType(key)) {
        return;
      }
      const vectorSource = sources[key]?.getSource?.();
      if (!vectorSource || typeof vectorSource.getFeatures !== 'function') {
        return;
      }

      vectorSource.getFeatures().forEach((feature: FeatureLike) => {
        try {
          rows.push(mapFeatureToPopupRow(feature));
        } catch (error) {
          console.error('Failed to build table row from feature:', error);
        }
      });
    });

    const uniqueRows = rows.filter((row, index, self) => {
      const identifier = row.location_id || row.userid || `${row.type}-${row.name}-${row.address}`;
      return self.findIndex((candidate) => {
        const candidateId = candidate.location_id || candidate.userid || `${candidate.type}-${candidate.name}-${candidate.address}`;
        return candidateId === identifier;
      }) === index;
    });

    const sortedRows = [...uniqueRows].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    let content = '';
    try {
      content = buildPopupTableContent(sortedRows, {
        page: 1,
        pageSize: 10,
        search: '',
        columnVisibility: tableColumnVisibility,
        showColumnToggle: true,
      });
    } catch (error) {
      console.error('Failed to build table content from map data:', error);
    }

    setPipelineDrawerEntries([]);
    setPipelineDrawerMetricsOverride(null);
    setTableRows(sortedRows);
    setTableSearch('');
    setTableDrawerHTML(content);
    setTableDrawerVisible(true);
  }, [
    buildPopupTableContent,
    computePipelineDrawerMetricsOverride,
    filters,
    frontsiteOrders,
    openFrontsiteDrawerOrders,
    mapFeatureToPopupRow,
    pipelineStageFilter,
    salesPipelineResponse,
    sources,
    tableColumnVisibility,
    activeProductBucketKey,
    productLocations,
    resolvedProductBuckets,
  ]);

  const fetchBoundaryData = async (country?: string, state?: string, city?: string) => {
  try {
    const baseUrl = 'https://nominatim.openstreetmap.org/search';
    const params = new URLSearchParams();
    // Use jsonv2 format for consistent address metadata
    // params.set('format', 'jsonv2');

    const headers = {
      'User-Agent': 'myapp/1.0 (it@dbi360.com)'
    };

    const norm = (s?: string) => (s || '').toLowerCase().replace(/\./g, '').trim();
    const isDCSynonym = (s?: string) => {
      const n = norm(s);
      return n === 'washington dc' || n === 'washington d c' || n === 'dc' || 
             n === 'district of columbia' || n === 'washington, dc' || 
             n === 'washington d.c' || n === 'washington d.c.';
    };

    // Normalize DC inputs
    const isDCState = isDCSynonym(state);
    const isDCCity = isDCSynonym(city) || (norm(city) === 'washington' && isDCSynonym(state));
    const canonicalDCState = 'District of Columbia';
    const canonicalDCCity = 'Washington';

    // Build query parts for logging/fallback and structured search params
    const queryParts: string[] = [];
    let boundaryLevel: 'city' | 'state' | 'country' | '' = '';

    if (city && state && country) {
      boundaryLevel = 'city';
      queryParts.push(country);
      queryParts.push(isDCState ? canonicalDCState : state);
      queryParts.push(isDCCity ? canonicalDCCity : city);
    } else if (state && country) {
      boundaryLevel = 'state';
      queryParts.push(country);
      queryParts.push(isDCState ? canonicalDCState : state);
    } else if (country) {
      boundaryLevel = 'country';
      queryParts.push(country);
    } else {
      console.warn('No location parameters provided');
      return null;
    }

    params.set('q', queryParts.join(', '));

    const url = `${baseUrl}?${params.toString()}&format=jsonv2&polygon_geojson=1`;

    const response = await fetch(url , {headers});
    if (!response.ok) {
      console.error('[Boundary] API error:', response.status, response.statusText);
      throw new Error('Failed to fetch boundary data');
    }

    const data: any[] = await response.json();

    if (!Array.isArray(data) || data.length === 0) {
      console.warn('No boundary candidates returned by Nominatim.');
      return null;
    }

    // Prefer administrative boundaries
    let candidates = data.filter((item: any) => 
      item && item.geojson && item.class === 'boundary' && item.type === 'administrative'
    );
    
    if (candidates.length === 0) {
      candidates = data.filter((item: any) => item && item.geojson);
    }

    if (candidates.length === 0) {
      console.warn('No candidates with geojson found');
      return null;
    }


    const strEq = (a?: string, b?: string) => norm(a) === norm(b);

    const isWithinDCBox = (bb: any) => {
      if (!bb || bb.length < 4) return false;
      const south = parseFloat(bb[0]);
      const north = parseFloat(bb[1]);
      const west = parseFloat(bb[2]);
      const east = parseFloat(bb[3]);
      if ([south, north, west, east].some(v => Number.isNaN(v))) return false;
      // Rough DC bounds
      return south > 38.4 && north < 39.1 && west > -77.3 && east < -76.7;
    };

    const computeBoundingBoxArea = (bb: any): number => {
      if (!bb || bb.length < 4) return 0;
      const south = parseFloat(bb[0]);
      const north = parseFloat(bb[1]);
      const west = parseFloat(bb[2]);
      const east = parseFloat(bb[3]);
      if ([south, north, west, east].some((value) => Number.isNaN(value))) {
        return 0;
      }
      return Math.abs((east - west) * (north - south));
    };

    const selectLargestCandidate = (items: any[]): any | null => {
      if (!Array.isArray(items) || items.length === 0) {
        return null;
      }

      let best: any | null = null;
      let bestArea = -Infinity;

      items.forEach((item) => {
        const area = computeBoundingBoxArea(item.boundingbox);
        if (area > bestArea) {
          bestArea = area;
          best = item;
        }
      });

      return best ?? items[0] ?? null;
    };

    const pickPreferredCandidate = (...lists: any[][]) => {
      for (const list of lists) {
        const candidate = selectLargestCandidate(list);
        if (candidate) {
          return candidate;
        }
      }
      return null;
    };

    let chosen: any | null = null;

    if (boundaryLevel === 'state' && state) {
      const desiredState = isDCState ? canonicalDCState : state;
      const stateTypes = new Set(['state', 'state_district', 'province', 'region', 'territory', 'administrative']);

      const matchesStateName = (item: any) => {
        const addressState =
          item.address?.state ||
          item.address?.region ||
          item.address?.province ||
          item.address?.territory ||
          '';
        const namePart = (item.display_name || '').split(',')[0];
        return (
          strEq(addressState, desiredState) ||
          strEq(namePart, desiredState) ||
          (item.display_name && item.display_name.toLowerCase().includes(desiredState.toLowerCase()))
        );
      };

      const matchesCountry = (item: any) =>
        country
          ? strEq(item.address?.country, country) ||
            (item.display_name && item.display_name.toLowerCase().includes(country.toLowerCase()))
          : true;

      const filtered = candidates.filter(
        (item: any) =>
          stateTypes.has((item.addresstype || '').toLowerCase()) &&
          matchesStateName(item) &&
          matchesCountry(item),
      );


      const fallbackByName = candidates.filter(
        (item: any) => matchesStateName(item) && matchesCountry(item),
      );
      const fallbackByType = candidates.filter(
        (item: any) => stateTypes.has((item.addresstype || '').toLowerCase()) && matchesCountry(item),
      );

      const preferredState = pickPreferredCandidate(filtered, fallbackByName, fallbackByType, candidates);

      // For DC, ensure we pick the DC polygon (east coast), not Washington State
      if (isDCState) {
        chosen =
          filtered.find((item: any) => isWithinDCBox(item.boundingbox)) ||
          candidates.find((item: any) => isWithinDCBox(item.boundingbox)) ||
          preferredState ||
          pickPreferredCandidate(fallbackByType, candidates);
      } else {
        chosen = preferredState;
      }
    } else if (boundaryLevel === 'city' && city) {
      const cityTypes = new Set(['city', 'town', 'village', 'municipality']);
      const desiredCity = isDCCity ? canonicalDCCity : city;
      const desiredState = isDCState ? canonicalDCState : state;

      const resolveCityName = (item: any) =>
        item.address?.city ||
        item.address?.town ||
        item.address?.village ||
        item.address?.municipality ||
        (item.display_name || '').split(',')[0];

      const matchesCityName = (item: any) =>
        strEq(resolveCityName(item), desiredCity) ||
        (item.display_name && item.display_name.toLowerCase().startsWith(desiredCity.toLowerCase()));

      const matchesState = (item: any) =>
        desiredState ? strEq(item.address?.state, desiredState) || strEq(item.address?.region, desiredState) : true;

      const matchesCountry = (item: any) =>
        country
          ? strEq(item.address?.country, country) ||
            (item.display_name && item.display_name.toLowerCase().includes(country.toLowerCase()))
          : true;

      const filtered = candidates.filter(
        (item: any) =>
          cityTypes.has((item.addresstype || '').toLowerCase()) &&
          matchesCityName(item) &&
          matchesState(item) &&
          matchesCountry(item),
      );


      const fallbackByName = candidates.filter(
        (item: any) => matchesCityName(item) && matchesState(item) && matchesCountry(item),
      );
      const fallbackByType = candidates.filter(
        (item: any) => cityTypes.has((item.addresstype || '').toLowerCase()) && matchesCountry(item),
      );

      const preferredCity = pickPreferredCandidate(filtered, fallbackByName, fallbackByType, candidates);

      if (isDCCity) {
        chosen = filtered.find((item: any) => isWithinDCBox(item.boundingbox))
          || candidates.find((item: any) => isWithinDCBox(item.boundingbox))
          || preferredCity
          || pickPreferredCandidate(fallbackByType, candidates);
      } else {
        chosen = preferredCity;
      }
    } else if (boundaryLevel === 'country' && country) {
      const countryMatches = candidates.filter((item: any) =>
        strEq(item.addresstype, 'country') && strEq(item.address?.country, country)
      );
      const typeMatches = candidates.filter((item: any) => strEq(item.addresstype, 'country'));
      chosen = pickPreferredCandidate(countryMatches, typeMatches, candidates);

    } else {
      chosen = selectLargestCandidate(candidates);
    }

    if (chosen) {

      if (chosen.geojson) {
        return chosen.geojson;
      }

      const bbox = Array.isArray(chosen.boundingbox) ? chosen.boundingbox : null;
      if (bbox && bbox.length === 4) {
        const [southRaw, northRaw, westRaw, eastRaw] = bbox;
        const south = parseFloat(southRaw);
        const north = parseFloat(northRaw);
        const west = parseFloat(westRaw);
        const east = parseFloat(eastRaw);

        if ([south, north, west, east].every((value) => Number.isFinite(value))) {
          return {
            type: 'Polygon',
            coordinates: [[
              [west, south],
              [west, north],
              [east, north],
              [east, south],
              [west, south],
            ]],
          };
        }
      }
    }

    console.warn('No suitable boundary found');
    return null;
  } catch (error) {
    console.error('Error fetching boundary data:', error);
    return null;
  }
};

  // Effect to update boundaries when filters change
  useEffect(() => {
    if (!mapInstance.current || !showMap) return;

    const updateBoundaries = async () => {
      // Clear existing boundary features
      boundarySource.clear();

      // Determine what boundary to show based on filters
      let shouldShowBoundary = false;
      let boundaryLevel = '';

      if (filters.city && filters.state && filters.country) {
        shouldShowBoundary = true;
        boundaryLevel = 'city';
      } else if (filters.state && filters.country) {
        shouldShowBoundary = true;
        boundaryLevel = 'state';
      } else if (filters.country) {
        shouldShowBoundary = true;
        boundaryLevel = 'country';
      }

      if (shouldShowBoundary) {
        try {
          const geojson = await fetchBoundaryData(filters.country, filters.state, filters.city);
          
          if (geojson) {
            // Create a simple polygon feature from coordinates if available
            if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
              let geometry;
              
              if (geojson.type === 'Polygon') {
                const coordinates = geojson.coordinates[0].map((coord: number[]) => 
                  fromLonLat([coord[0], coord[1]])
                );
                geometry = new Polygon([coordinates]);
              } else if (geojson.type === 'MultiPolygon') {
                const polygons = geojson.coordinates.map((polygon: number[][][]) => 
                  polygon.map((ring: number[][]) => 
                    ring.map((coord: number[]) => fromLonLat([coord[0], coord[1]]))
                  )
                );
                geometry = new MultiPolygon(polygons);
              }

              if (geometry) {
                const feature = new Feature({
                  geometry,
                  properties: {
                    level: boundaryLevel,
                    name: filters.city || filters.state || filters.country
                  }
                });

                boundarySource.addFeature(feature);
                
                // Fit the map to the boundary
                const extent = geometry.getExtent();
                mapInstance.current?.getView().fit(extent, {
                  duration: 1000,
                  padding: [50, 50, 50, 50]
                });
              }
            }
          }
        } catch (error) {
          console.error('Error updating boundaries:', error);
        }
      }

      // Show/hide boundary layer
      boundaryLayer.setVisible(shouldShowBoundary);
    };

    // Debounce boundary updates to avoid too many API calls
    const timeoutId = setTimeout(updateBoundaries, 500);
    
    return () => clearTimeout(timeoutId);
  }, [filters.country, filters.state, filters.city, showMap, boundarySource, boundaryLayer]);

  // Handle location update
  const handleLocationUpdated = () => {
    // Refetch locations data to get updated information
    // This will trigger a re-render with the updated data
    window.location.reload(); // Simple approach to refresh the data
  };

  // Handle edit modal close
  const handleEditModalClose = () => {
    setEditModalVisible(false);
    setEditingLocationData(null);
  };

  // Function to fetch balance for a single partner
  const handleFetchSinglePartnerBalance = async (partnerId: number, buttonElement: HTMLElement) => {
    if (!partnerId) return;

    // Show spinner on button
    buttonElement.innerHTML = '<div style="width: 16px; height: 16px; border: 2px solid #ffffff; border-top: 2px solid transparent; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto;"></div>';
    buttonElement.style.pointerEvents = 'none';

    // Add spinner animation CSS if not already added
    if (!document.getElementById('spinner-style')) {
      const style = document.createElement('style');
      style.id = 'spinner-style';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    try {
      const clientSummary = await fetchClientSummary([partnerId]);
      
      if (clientSummary.success && clientSummary.data.length > 0) {
        const clientData = clientSummary.data.find(client => client.partner_id === partnerId);
        if (clientData) {
          // Find the balance amount container and update it
          const balanceAmountDiv = buttonElement.closest('.popup-stat')?.querySelector('.balance-amount');
          const balanceAmountStrong = balanceAmountDiv?.querySelector('strong');
          
          if (balanceAmountDiv && balanceAmountStrong) {
            balanceAmountStrong.textContent = clientData.formatted_closing_balance;
            
            // Hide button and show balance amount
            buttonElement.style.display = 'none';
            (balanceAmountDiv as HTMLElement).style.display = 'block';
          }
        }
      } else {
        // Still hide button and show "No data available"
        const balanceAmountDiv = buttonElement.closest('.popup-stat')?.querySelector('.balance-amount');
        const balanceAmountStrong = balanceAmountDiv?.querySelector('strong');
        
        if (balanceAmountDiv && balanceAmountStrong) {
          balanceAmountStrong.textContent = 'No data available';
          buttonElement.style.display = 'none';
          (balanceAmountDiv as HTMLElement).style.display = 'block';
        }
      }
    } catch (error) {
      console.error('Error fetching client summary:', error);
      // Show error message
      const balanceAmountDiv = buttonElement.closest('.popup-stat')?.querySelector('.balance-amount');
      const balanceAmountStrong = balanceAmountDiv?.querySelector('strong');
      
      if (balanceAmountDiv && balanceAmountStrong) {
        balanceAmountStrong.textContent = 'Error loading balance';
        balanceAmountStrong.style.color = '#ff4d4f';
        buttonElement.style.display = 'none';
        (balanceAmountDiv as HTMLElement).style.display = 'block';
      }
    }
  };

  const isAllDates = filters.dateRangePreset === 'all-dates';
  const insightsDateFrom = isAllDates ? '2015-01-01' : filters.startDate;
  const insightsDateTo = isAllDates ? formatDateValue(new Date()) : filters.endDate;

  return (
    <Box>
      <div ref={mapRef} className="map-container" />
      <div ref={popupRef} className="ol-popup" />
      <IconButton
        sx={{
          position: 'fixed',
          right: 60,
          top: 80,
          height: '40px',
          minWidth: '40px',
          padding: '8px',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontSize: '1rem',
          color: '#222',
          '&:hover': {
            backgroundColor: '#f5f5f5'
          },
          zIndex: 1000
        }}
        onClick={handleShowListClick}
      >
        <MenuIcon /> Show List
      </IconButton>
      <Box 
        sx={{
          position: 'fixed',
          right: 175,
          top: 80,
          zIndex: 1000
        }}
      >
        <FilterSection
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </Box>

      {mapInstance.current && popup && (
        <Drawer
          title="Location"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={1200}
          bodyStyle={{ padding: '16px' }}
        >
          <Sidebar 
            filters={filters} 
            map={mapInstance.current}
            clientSource={sources.client}
            vendorSource={sources.vendor}
            prospectSource={sources.prospect}
            popup={popup}
            popupRef={popupRef}
          />
        </Drawer>

      )}
      {filters.mapView === 'revenue' && !isAllDates && !revenueTargetCardDismissed && revenueTargetCardData && (
        <RevenueTargetMetricsCard
          visible
          title={`Revenue Target Metrics${filters.country ? ` - ${filters.country}` : ''}`}
          highlightedState={revenueTargetCardData.highlightedState}
          totalTarget={revenueTargetCardData.totalTarget}
          totalAchieved={revenueTargetCardData.totalAchieved}
          totalRemaining={revenueTargetCardData.totalRemaining}
          achievementPercent={revenueTargetCardData.achievementPercent}
          breakdown={revenueTargetCardData.breakdown}
          onViewBreakdown={handleOpenRevenueTargetDrawer}
          onClose={() => setRevenueTargetCardDismissed(true)}
        />
      )}
      {!isAllDates && (
        <RevenueTargetDrawer
          open={revenueTargetDrawerVisible}
          onClose={handleCloseRevenueTargetDrawer}
          cardData={revenueTargetCardData}
          rows={revenueTargetDrawerRows}
          totalPages={revenueTargetDrawerTotalPages}
          currentPage={revenueTargetDrawerSafePage}
          onPageChange={(page) => setRevenueTargetDrawerPage(page)}
          onViewCompanies={handleOpenRevenueCompaniesDetail}
          onViewReps={handleOpenRevenueRepsDetail}
          onCompanyLink={handleCompanyLinkClick}
          formatTargetPercent={formatTargetPercent}
        />
      )}
      {filters.mapView === 'product' && (
        <ProductLegend
          summary={productSummary}
          bucketSummaries={productBucketSummaries}
          isLoading={isLoadingProductLocations}
          activeBucketKey={activeProductBucketKey}
          onToggleBucket={handleProductBucketToggle}
        />
      )}
      {filters.mapView === 'revenue' && <RevenueLegend />}

      {filters.mapView === 'sales-pipeline' && (
        <SalesPipelineLegend
          pipelineStageFilter={pipelineStageFilter}
          onToggleStage={handlePipelineStageToggle}
          pipelineSummary={pipelineSummary}
          pipelineStageSequence={PIPELINE_STAGE_SEQUENCE}
          pipelineStageColors={SALES_PIPELINE_STAGE_COLORS}
          formatShortCurrency={formatShortCurrency}
        />
      )}
      {filters.mapView === 'supply-hub' && (
        <SupplyHubLegend
          shippingTotals={supplyHubShippingTotals}
          totalPurchaseOrders={supplyHubDisplayTotalPurchaseOrders}
          warehouseCount={supplyHubDisplayWarehouses.length}
          vendorCount={supplyHubDisplayVendorGroups.length}
          activeMode={supplyHubModeFilter}
          onModeChange={handleSupplyHubModeChange}
        />
      )}
      {filters.mapView === 'standard' && (
        <StandardMapView
          filters={filters}
          locationTypeSummary={locationTypeSummary}
          locationTypeOrder={LOCATION_TYPE_ORDER}
          locationTypeMeta={LOCATION_TYPE_META}
          onToggleLocationType={handleLocationTypeChipToggle}
        />
      )}
      {filters.mapView === 'frontsite' && (
        <FrontsiteMapView analytics={frontsiteAnalytics} />
      )}
        <Drawer
          title={
            filters.mapView === 'sales-pipeline'
              ? `Sales Pipeline${(() => {
                  const labelParts = [filters.state, filters.country]
                    .map((part) => (part || '').trim())
                    .filter((part) => part.length > 0);
                  return labelParts.length ? ` (${labelParts.join(', ')})` : '';
                })()}`
              : (
                <span>
                  Location Listing {(() => { const label = [filters.city, filters.state, filters.country].filter(Boolean).join(', '); return label ? '(' + label + ')' : ''; })()}
                </span>
              )
          }
        placement="right"
        width={filters.mapView === 'sales-pipeline' ? 1350 : 1350}
        push={false}
        open={tableDrawerVisible}
        onClose={() => {
          setTableDrawerVisible(false);
          setPipelineDrawerEntries([]);
          setPipelineDrawerContext('overall');
          setPipelineDrawerMetricsOverride(null);
          setFrontsiteDrawerOrders([]);
          setFrontsiteDetailDrawerVisible(false);
          setFrontsiteDetailOrder(null);
        }}
        destroyOnClose
      >
        {filters.mapView === 'sales-pipeline' ? (
          <SalesPipelineDrawerContent
            hasEntries={pipelineDrawerEntries.length > 0}
            aggregates={pipelineDrawerAggregates}
            pipelineSummary={pipelineSummary}
            drawerContext={pipelineDrawerContext}
            metricsOverride={pipelineDrawerMetricsOverride}
            rows={pipelineDrawerTableRows}
            formatCurrency={formatCurrency}
            formatPipelineCount={formatPipelineCount}
            hexToRgba={hexToRgba}
          />
        ) : filters.mapView === 'frontsite' ? (
          <FrontsiteDrawerContent
            orders={frontsiteDrawerOrders}
            isLoading={isLoadingFrontsite}
            formatCurrency={formatCurrency}
            formatExactCurrency={formatExactCurrency}
            onViewOrder={handleFrontsiteOrderView}
          />
        ) : (
          <>
        {/* Metrics summary per current listing (table rows + search) */}
        <Box sx={{ mb: 2 }}>
          {(() => {
            const { avgDeal, revenue, clientsCount, totalTarget, totalAchievement, targetPercent, targetYears } = revenueDrawerMetrics;
            const targetYearLabel = (() => {
              const normalizedYears = (targetYears || [])
                .map((value) => String(value ?? '').trim())
                .filter((value) => value.length > 0);
              if (normalizedYears.length === 0) {
                return 'N/A';
              }
              return Array.from(new Set(normalizedYears)).join(', ');
            })();
            const targetRemaining = Math.max(totalTarget - totalAchievement, 0);
            const targetPercentLabel = Number.isFinite(targetPercent) ? `${targetPercent.toFixed(1)}% Achieved` : '0.0% Achieved';
            const targetPercentTitle = Number.isFinite(targetPercent) ? `${targetPercent.toFixed(2)}% Achieved` : '0.00% Achieved';

            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    gridTemplateColumns: {
                      xs: 'repeat(1, minmax(0,1fr))',
                      sm: 'repeat(2, minmax(0,1fr))',
                      md: 'repeat(3, minmax(0,1fr))',
                    },
                  }}
                >
                  <Box sx={{ background: '#e8f0fe', p: 1.25, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ fontSize: 12, color: '#1a73e8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Avg Deal</Box>
                    <Box sx={{ fontSize: 18, fontWeight: 700, color: '#1a73e8' }} title={formatExactCurrency(avgDeal)}>
                      {formatCurrency(avgDeal)}
                    </Box>
                  </Box>
                  <Box sx={{ background: '#fff7e6', p: 1.25, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ fontSize: 12, color: '#f29900', textTransform: 'uppercase', letterSpacing: 0.4 }}>Revenue</Box>
                    <Box sx={{ fontSize: 18, fontWeight: 700, color: '#f29900' }} title={formatExactCurrency(revenue)}>
                      {formatCurrency(revenue)}
                    </Box>
                  </Box>
                  <Box sx={{ background: '#fce8e6', p: 1.25, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ fontSize: 12, color: '#d93025', textTransform: 'uppercase', letterSpacing: 0.4 }}>Clients</Box>
                    <Box sx={{ fontSize: 18, fontWeight: 700, color: '#d93025' }} title={formatExactNumber(clientsCount)}>
                      {clientsCount}
                    </Box>
                  </Box>
                </Box>
                {!isAllDates && (
                <div>
                  {/* Section Title */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">
                      Target Progress
                    </div>
                    <div 
                      className="text-sm font-bold text-emerald-700 bg-emerald-100 px-5 py-1.5 rounded-full"
                      title={targetPercentTitle}
                    >
                      {targetPercentLabel}
                    </div>
                  </div>

                  {/* 4 Boxes Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Box 1 - Total Target */}
                    <div className="flex flex-col gap-1.5 bg-emerald-50 p-4 rounded-lg">
                      <div className="text-xs text-emerald-700">Total Target</div>
                      <div 
                        className="text-lg font-bold text-emerald-600" 
                        title={formatExactCurrency(totalTarget)}
                      >
                        {formatCurrency(totalTarget)}
                      </div>
                    </div>

                    {/* Box 2 - Total Achievement */}
                    <div className="flex flex-col gap-1.5 bg-blue-50 p-4 rounded-lg">
                      <div className="text-xs text-blue-700">Total Achievement</div>
                      <div 
                        className="text-lg font-bold text-blue-600" 
                        title={formatExactCurrency(totalAchievement)}
                      >
                        {formatCurrency(totalAchievement)}
                      </div>
                    </div>

                    {/* Box 3 - Target Remaining */}
                    <div className="flex flex-col gap-1.5 bg-amber-50 p-4 rounded-lg">
                      <div className="text-xs text-amber-700">Target Remaining</div>
                      <div 
                        className="text-lg font-bold text-amber-600" 
                        title={formatExactCurrency(targetRemaining)}
                      >
                        {formatCurrency(targetRemaining)}
                      </div>
                    </div>

                    {/* Box 4 - Target Year */}
                    <div className="flex flex-col gap-1.5 bg-purple-50 p-4 rounded-lg">
                      <div className="text-xs text-purple-700">Target Year</div>
                      <div className="text-base font-semibold text-purple-600">
                        {targetYearLabel}
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </Box>
            );
          })()}
        </Box>

        {/* Top 10 Client Insights - drawer trigger */}
        {(filters.mapView === 'revenue' || filters.mapView === 'standard') && (
          <RevenueDrawerExtras
            topClientRows={topClientRows}
            churnedClients={churnedClients}
            churnedSummary={churnedSummary}
            onOpenTopClients={() => setTopClientsDrawerVisible(true)}
            onOpenChurned={() => setChurnedDrawerVisible(true)}
          />
        )}
        {filters.mapView === 'product' && (
          <ProductDrawerExtras
            summary={productSummary}
            topProducts={productTopProducts}
            cityInsights={productCityInsights}
            onOpenTopProducts={() => setTopProductsDrawerVisible(true)}
            onOpenCityInsights={() => setCityInsightsDrawerVisible(true)}
            isLoading={isLoadingProductLocations}
          />
        )}
        {filters.mapView === 'standard' || filters.mapView === 'revenue' ? (
          <StandardMapDrawerContent
            rows={tableRows}
            search={tableSearch}
            onSearchChange={(value) => setTableSearch(value)}
            columnVisibility={activeTabColumnVisibility}
            onColumnVisibilityChange={(visibility) =>
              setTableColumnVisibilityByTab((prev) => ({
                ...prev,
                [drawerActiveTab]: visibility,
              }))
            }
            onEditLocation={handleStandardRowEdit}
            activeTab={drawerActiveTab}
            onTabChange={handleDrawerTabChange}
            onClientSelect={handleClientSelect}
            allowedTabs={filters.mapView === 'revenue' ? ['client'] : undefined}
          />
        ) : (
          <div ref={tableDrawerRef} dangerouslySetInnerHTML={{ __html: tableDrawerHTML }} />
        )}
      
          </>
        )}
      </Drawer>

      <Drawer
        title="Order Details"
        placement="right"
        width={860}
        open={frontsiteDetailDrawerVisible}
        onClose={handleFrontsiteOrderDetailClose}
        destroyOnClose
      >
        <FrontsiteOrderDetails
          order={frontsiteDetailOrder}
          formatCurrency={formatCurrency}
          formatExactCurrency={formatExactCurrency}
        />
      </Drawer>

      <CustomerInsightsDrawer
        open={customerInsightsOpen}
        onClose={() => setCustomerInsightsOpen(false)}
        customerId={customerInsightsContext?.id}
        customerName={customerInsightsContext?.name}
        dateFrom={insightsDateFrom}
        dateTo={insightsDateTo}
      />

      <Drawer
        title="Top 10 Client Insights"
        placement="right"
        width={1200}
        open={topClientsDrawerVisible}
        onClose={() => setTopClientsDrawerVisible(false)}
        destroyOnClose
      >
        <RevenueTopClientsContent
          rows={topClientRows}
          formatCurrency={formatCurrency}
          parseChurnAmount={parseChurnAmount}
          getRevenueTierColor={getRevenueTierColor}
          totalRevenue={revenueDrawerMetrics.revenue}
          onClientSelect={handleClientSelect}
        />
      </Drawer>
      <Drawer
        title="Churned Clients"
        placement="right"
        width={1200}
        open={churnedDrawerVisible}
        onClose={() => setChurnedDrawerVisible(false)}
        destroyOnClose
      >
        <RevenueChurnedContent
          rows={churnedClients}
          paginatedRows={paginatedChurnedClients}
          churnedTotalPages={churnedTotalPages}
          currentPage={churnedPageSafe}
          onPageChange={setChurnedPage}
          formatCurrency={formatCurrency}
          parseChurnAmount={parseChurnAmount}
          parseInvoiceDate={parseInvoiceDate}
          formatInvoiceDate={formatInvoiceDate}
          formatRelativeDate={formatRelativeDate}
          computeOpenSOMetrics={computeOpenSOMetrics}
          totalRevenue={revenueDrawerMetrics.revenue}
          onClientSelect={handleClientSelect}
        />
      </Drawer>
      <Drawer
        title={`${revenueCompanyDetail.stateName ?? 'State'} Companies`}
        placement="right"
        width={860}
        open={revenueCompanyDetail.open}
        onClose={handleCloseRevenueCompaniesDetail}
        destroyOnClose
      >
        {revenueCompanyDetail.rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No company details available for this state.</Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: 'repeat(1, minmax(0,1fr))', sm: 'repeat(3, minmax(0,1fr))' },
              }}
            >
              <Box sx={{ background: '#e8f0fe', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#1a73e8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Companies
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#1a73e8' }}>
                  {formatExactNumber(revenueCompanyDetail.rows.length)}
                </Box>
              </Box>
              <Box sx={{ background: '#e6f4ea', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#0b8043', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Target
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#0b8043' }} title={formatExactCurrency(revenueCompanyTotals.totalTarget)}>
                  {formatCurrency(revenueCompanyTotals.totalTarget)}
                </Box>
              </Box>
              <Box sx={{ background: '#fef3c7', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Achieved
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#b45309' }} title={formatExactCurrency(revenueCompanyTotals.totalAchieved)}>
                  {formatCurrency(revenueCompanyTotals.totalAchieved)}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1.2fr 0.7fr 0.85fr 0.85fr',
                gap: 1,
                px: 1,
                py: 1,
                backgroundColor: '#f1f5f9',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              <Box>Company</Box>
              <Box>Coverage</Box>
              <Box sx={{ textAlign: 'center' }}>Locations</Box>
              <Box>Target</Box>
              <Box>Achieved</Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {revenueCompanyDetail.rows.map((companyRow) => {
                const repsLabel = companyRow.salesReps.length > 0 ? companyRow.salesReps.join(', ') : 'Unassigned';
                const coverageParts = [companyRow.city, companyRow.state].filter(Boolean).join(', ');

                return (
                  <Box
                    key={`${companyRow.userid || companyRow.company}-${companyRow.city || ''}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.5fr 1.2fr 0.7fr 0.85fr 0.85fr',
                      gap: 1,
                      px: 1,
                      py: 1,
                      borderRadius: 1,
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#fff',
                      fontSize: 13,
                      color: '#0f172a',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {companyRow.userid ? (
                        <a
                          href="#"
                          onClick={(event) => handleCompanyLinkClick(event, companyRow.userid)}
                          style={{ color: '#1a73e8', textDecoration: 'underline', fontWeight: 600 }}
                        >
                          {companyRow.company}
                        </a>
                      ) : (
                        <span style={{ fontWeight: 600 }}>{companyRow.company}</span>
                      )}
                      <span style={{ fontSize: 12, color: '#64748b' }}>{coverageParts || 'Location unavailable'}</span>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, fontSize: 12, color: '#475569' }}>
                      <span>{repsLabel}</span>
                      <span style={{ color: '#94a3b8' }}>{companyRow.salesReps.length === 1 ? 'Sales contact' : 'Sales contacts'}</span>
                    </Box>
                    <Box sx={{ textAlign: 'center', fontWeight: 600 }}>
                      {formatExactNumber(companyRow.locationCount)}
                    </Box>
                    <Box sx={{ fontWeight: 600, color: '#1a237e' }} title={formatExactCurrency(companyRow.target)}>
                      {formatCurrency(companyRow.target)}
                    </Box>
                    <Box sx={{ fontWeight: 600, color: '#0b8043' }} title={formatExactCurrency(companyRow.achieved)}>
                      {formatCurrency(companyRow.achieved)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Drawer>
      <Drawer
        title={`${revenueRepDetail.stateName ?? 'State'} Sales Reps`}
        placement="right"
        width={720}
        open={revenueRepDetail.open}
        onClose={handleCloseRevenueRepsDetail}
        destroyOnClose
      >
        {revenueRepDetail.rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No sales rep performance data for this state.</Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: 'repeat(1, minmax(0,1fr))', sm: 'repeat(2, minmax(0,1fr))' },
              }}
            >
              <Box sx={{ background: '#e8f0fe', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#1a73e8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Sales Reps
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#1a73e8' }}>
                  {formatExactNumber(revenueRepDetail.rows.length)}
                </Box>
              </Box>
              <Box sx={{ background: '#e6f4ea', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#0b8043', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Revenue
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#0b8043' }} title={formatExactCurrency(revenueRepTotals.totalRevenue)}>
                  {formatCurrency(revenueRepTotals.totalRevenue)}
                </Box>
              </Box>
              <Box sx={{ background: '#fef3c7', p: 1.25, borderRadius: 1 }}>
                <Box sx={{ fontSize: 12, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Accounts Managed
                </Box>
                <Box sx={{ fontSize: 18, fontWeight: 700, color: '#b45309' }}>
                  {formatExactNumber(revenueRepTotals.totalAccounts)}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 0.8fr 0.9fr 1.1fr',
                gap: 1,
                px: 1,
                py: 1,
                backgroundColor: '#f1f5f9',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                color: '#475569',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              <Box>Sales Rep</Box>
              <Box>Accounts</Box>
              <Box>Revenue</Box>
              <Box>Top Geography</Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {revenueRepDetail.rows.map((rep) => (
                <Box
                  key={`${rep.name}-${rep.companyCount}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 0.8fr 0.9fr 1.1fr',
                    gap: 1,
                    px: 1,
                    py: 1,
                    borderRadius: 1,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    fontSize: 13,
                    color: '#0f172a',
                  }}
                >
                  <Box sx={{ fontWeight: 600 }}>{rep.name}</Box>
                  <Box sx={{ fontWeight: 600, color: '#1f2937' }}>{formatExactNumber(rep.companyCount)}</Box>
                  <Box sx={{ fontWeight: 600, color: '#0b8043' }} title={formatExactCurrency(rep.revenue)}>
                    {formatCurrency(rep.revenue)}
                  </Box>
                  <Box sx={{ fontSize: 12, color: '#64748b' }}>
                    {rep.topCity ? `${rep.topCity}${rep.topCityCount ? ` (${rep.topCityCount})` : ''}` : 'No dominant city'}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Drawer>
      <Drawer
        title={supplyHubDrawerTitle}
        placement="right"
        width={960}
        open={filters.mapView === 'supply-hub' && supplyHubDrawerVisible}
        onClose={() => {
          setSupplyHubDrawerVisible(false);
          setSupplyHubDrawerContext(null);
        }}
        destroyOnClose
      >
        {supplyHubDrawerContext && (
          <SupplyHubDrawerContent context={supplyHubDrawerContext} shippingModeFilter={supplyHubModeFilter} />
        )}
      </Drawer>
      <ProductMapDrawer
        open={productDrawerVisible}
        onClose={() => setProductDrawerVisible(false)}
        title={productDrawerTitle || 'Product Insights'}
        locations={productDrawerLocations}
        startDate={filters.startDate}
        endDate={filters.endDate}
      />
      <ProductTopProductsDrawer
        open={topProductsDrawerVisible}
        onClose={() => setTopProductsDrawerVisible(false)}
        topProducts={productTopProducts}
        summary={productSummary}
        isLoading={isLoadingProductLocations}
      />
      <ProductCityInsightsDrawer
        open={cityInsightsDrawerVisible}
        onClose={() => setCityInsightsDrawerVisible(false)}
        insights={productCityInsights}
        isLoading={isLoadingProductLocations}
      />
      <EditLocationModal
        visible={editModalVisible}
        onClose={handleEditModalClose}
        locationData={editingLocationData}
        onLocationUpdated={handleLocationUpdated}
      />
      <PrimaryMetricModal
        visible={primaryMetricModalVisible}
        onClose={onPrimaryMetricModalClose}
        locationName={
          primaryMetricsData?.data?.location ? 
            [primaryMetricsData.data.location.city, primaryMetricsData.data.location.state, primaryMetricsData.data.location.country]
              .filter(Boolean).join(', ') :
            [filters.city, filters.state, filters.country].filter(Boolean).join(', ') || 'All Locations'
        }
        metrics={
          primaryMetricsData?.data?.metrics || {
            clients: locationsResponse.locations.length,
            prospects: prospects.length,
            vendors: vendors.length,
            conversionRate: prospects.length > 0 ? (locationsResponse.locations.length / prospects.length) * 100 : 0,
            revenue: typeof locationsResponse.total_amount === 'string' ? 
              parseFloat(locationsResponse.total_amount.replace(/[^0-9.-]+/g, '')) || 0 : 
              locationsResponse.total_amount || 0,
            yoyGrowth: 18.5,
            avgDeal: typeof locationsResponse.total_amount === 'string' ? 
              (parseFloat(locationsResponse.total_amount.replace(/[^0-9.-]+/g, '')) || 0) / Math.max(locationsResponse.locations.length, 1) : 
              (locationsResponse.total_amount || 0) / Math.max(locationsResponse.locations.length, 1),
            custProspectRatio: prospects.length > 0 ? locationsResponse.locations.length / prospects.length : 0
          }
        }
        isLoading={isLoadingPrimaryMetrics}
      />
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1100,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default MapComponent;
